import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  LinearProgress,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  TextField,
  InputBase,
  Button,
  Alert,
  IconButton,
  Popover,
  Skeleton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon, InfoOutlined as InfoIcon, CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon, CheckBox as CheckBoxIcon, Delete as DeleteIcon, Add as AddIcon, Lan as LanIcon, WarningAmberRounded as WarningAmberRoundedIcon } from '@mui/icons-material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { HabitSummary, HabitDefinition, HabitVersionInfo, UpdateHabitGoalRequest, UpdateHabitNameRequest, CreateNewVersionResponse, PublishHabitVersionResponse, BlockSequence, BlockNodeGraphResponse } from '../types/api';
import { habitService, blockService } from '../services/api';
import GenericModal from './GenericModal';
import CreateHabitModal from './CreateHabitModal';
import ContextMenu, { ContextMenuPosition } from './ContextMenu';
import SaveActionButton from './SaveActionButton';
import HoverActionIconButton from './HoverActionIconButton';
import ParentChildGoalEditor from './ParentChildGoalEditor';
import { getCategoryColor } from '../utils/habitColors';
import { MarkdownRenderer, markdownLinkComponent } from './MarkdownText';
import { AIRMASK_GREEN, AIRMASK_DARK, AIRMASK_SURFACE_GRAY } from '../constants/colors';
import BlockSequenceDisplay from './BlockSequenceDisplay';
import BlockNodeGraph from './BlockNodeGraph';
import { useIsCoarsePointer, useMobileKeyboardVisible } from '../hooks/useMobileKeyboardVisible';

interface HabitDetailModalProps {
  open: boolean;
  habit: HabitSummary | null;
  habitVersions?: HabitVersionInfo[];
  loading?: boolean;
  isChildHabit?: boolean;
  currentParentHabitDisplayName?: string;
  onClose: () => void;
  onHabitUpdated?: () => void;
  onHabitDeleted?: () => void;
  /** Called instead of onClose when the habit's category crosses the Manage boundary.
   * The parent should show the skeleton loader and re-fetch the habit immediately. */
  onHabitReloadRequired?: () => void;
  /** Optional callback to open a habit by URL route (used for child habit deep links). */
  onOpenHabitById?: (habitId: string) => void;
}

const DO_DAY_TASKS_HABIT_ID = '38BVMOp50CJualXd5s9oRScWxo7';
const LONG_PRESS_DURATION_MS = 550;
const TOUCH_MOVE_CANCEL_THRESHOLD_PX = 10;
const LONG_PRESS_CLOSE_GUARD_MS = 250;
const HOVER_ICON_LINGER_MS = 850;
const CHILD_HABIT_NAME_SEPARATOR = ': ';

const extractChildHabitNameSuffix = (
  habitDisplayName: string,
  parentHabitDisplayName?: string | null,
): string => {
  if (parentHabitDisplayName) {
    const exactPrefix = `${parentHabitDisplayName}${CHILD_HABIT_NAME_SEPARATOR}`;
    if (habitDisplayName.startsWith(exactPrefix)) {
      return habitDisplayName.slice(exactPrefix.length);
    }
  }

  const separatorIndex = habitDisplayName.lastIndexOf(CHILD_HABIT_NAME_SEPARATOR);
  if (separatorIndex === -1) {
    return habitDisplayName;
  }

  return habitDisplayName.slice(separatorIndex + CHILD_HABIT_NAME_SEPARATOR.length);
};

const formatChildHabitDisplayName = (habitDisplayName: string, parentHabitDisplayName?: string | null): string => {
  if (!parentHabitDisplayName || !habitDisplayName.includes(CHILD_HABIT_NAME_SEPARATOR)) {
    return habitDisplayName;
  }

  return `${parentHabitDisplayName}${CHILD_HABIT_NAME_SEPARATOR}${extractChildHabitNameSuffix(habitDisplayName, parentHabitDisplayName)}`;
};

// Helper function to generate tooltip text for goal info icon
const getGoalTooltipText = (
  goalType: 'THRESHOLD' | 'RANGE' | 'AVERAGE',
  goal: { type: 'THRESHOLD'; value: number } | { type: 'RANGE'; min: number; max: number } | { type: 'AVERAGE'; min: number; max: number },
  goalUnit: string,
  intervalUnit: string
): string => {
  const formattedInterval = intervalUnit.toLowerCase();
  if (goalType === 'THRESHOLD' && 'value' in goal) {
    return `Threshold: At least ${goal.value} ${goalUnit} per ${formattedInterval}`;
  } else if ((goalType === 'RANGE' || goalType === 'AVERAGE') && 'min' in goal && 'max' in goal) {
    if (goal.min === goal.max) {
      return `${goalType === 'AVERAGE' ? 'Average' : 'Range'}: Exactly ${goal.max} per ${formattedInterval}`;
    } else {
      return `${goalType === 'AVERAGE' ? 'Average' : 'Range'}: More than ${goal.min}, no greater than ${goal.max} (${goalUnit} per ${formattedInterval})`;
    }
  }
  return 'Goal information';
};

const HabitDetailModal: React.FC<HabitDetailModalProps> = ({
  open,
  habit,
  habitVersions = [],
  loading = false,
  isChildHabit = false,
  currentParentHabitDisplayName,
  onClose,
  onHabitUpdated,
  onHabitDeleted,
  onHabitReloadRequired,
  onOpenHabitById,
}) => {
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [habitDefinition, setHabitDefinition] = useState<HabitDefinition | null>(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionText, setDescriptionText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastModifiedAt, setLastModifiedAt] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<null | HTMLElement>(null);
  const [isHoveringGoal, setIsHoveringGoal] = useState(false);
  const [showDescriptionActions, setShowDescriptionActions] = useState(false);
  const isCoarsePointer = useIsCoarsePointer();
  const isMobileKeyboardVisible = useMobileKeyboardVisible();
  const hideEditActionButtons = isCoarsePointer || isMobileKeyboardVisible;
  const [showNewDayAction, setShowNewDayAction] = useState(false);
  const [isHoveringName, setIsHoveringName] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameText, setNameText] = useState('');
  const [isNameSaving, setIsNameSaving] = useState(false);
  const [nameSaveError, setNameSaveError] = useState<string | null>(null);
  const [localHabitName, setLocalHabitName] = useState<string | null>(null);
  const [isHoveringBlocks, setIsHoveringBlocks] = useState(false);
  const [isHoveringCompletedBlocks, setIsHoveringCompletedBlocks] = useState(false);
  const [isEditingBlocks, setIsEditingBlocks] = useState(false);
  const [isEditingCompletedBlocks, setIsEditingCompletedBlocks] = useState(false);
  const [blockSequences, setBlockSequences] = useState<BlockSequence[]>([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [blocksError, setBlocksError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<BlockNodeGraphResponse | null>(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [isChildHabitCreateOpen, setIsChildHabitCreateOpen] = useState(false);
  const [childHabitDefinitions, setChildHabitDefinitions] = useState<HabitDefinition[]>([]);
  const [childHabitProgressById, setChildHabitProgressById] = useState<Record<string, HabitSummary>>({});
  const [activeChildHabit, setActiveChildHabit] = useState<{
    habit: HabitSummary;
    habitVersions: HabitVersionInfo[];
    parentHabitDisplayName: string;
  } | null>(null);
  const [resolvedParentHabitDisplayName, setResolvedParentHabitDisplayName] = useState<string | null>(currentParentHabitDisplayName ?? null);
  const [childGoalAmounts, setChildGoalAmounts] = useState<Record<string, { threshold?: string; min?: string; max?: string }>>({});
  const [graphError, setGraphError] = useState<string | null>(null);
  const longPressTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const touchStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const longPressOpenedAtRef = React.useRef<number>(0);
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [selectedGoalType, setSelectedGoalType] = useState<'RANGE' | 'THRESHOLD' | 'AVERAGE'>('THRESHOLD');
  const [goalUnit, setGoalUnit] = useState<string>('');
  const [intervalUnit, setIntervalUnit] = useState<string>('');
  const [thresholdValue, setThresholdValue] = useState<string>('');
  const [rangeMin, setRangeMin] = useState<string>('');
  const [rangeMax, setRangeMax] = useState<string>('');
  const [goalError, setGoalError] = useState<string>('');
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition | null>(null);
  const [isPublishingVersion, setIsPublishingVersion] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [manageCategoryConfirmOpen, setManageCategoryConfirmOpen] = useState(false);
  const [manageCategoryConfirmStep, setManageCategoryConfirmStep] = useState<1 | 2>(1);
  const [manageCategoryConfirmText, setManageCategoryConfirmText] = useState('');
  const [isManageCategorySaving, setIsManageCategorySaving] = useState(false);
  const [manageCategoryError, setManageCategoryError] = useState<string | null>(null);
  const [pendingCategoryRequest, setPendingCategoryRequest] = useState<{
    lastModifiedAt: string;
    versionSK: string;
    isPublished: boolean;
  } | null>(null);
  const [isCreatingVersion, setIsCreatingVersion] = useState(false);
  const [versionCreationError, setVersionCreationError] = useState<React.ReactNode | null>(null);
  const [versionSnackbarSeverity, setVersionSnackbarSeverity] = useState<'success' | 'warning' | 'error'>('warning');
  const editFormRef = useRef<HTMLDivElement>(null);
  const categoryChipRef = useRef<HTMLDivElement>(null);
  const descriptionContainerRef = useRef<HTMLDivElement>(null);
  const blocksContainerRef = useRef<HTMLDivElement>(null);
  const completedBlocksContainerRef = useRef<HTMLDivElement>(null);
  const descriptionActionsHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const newDayHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nameHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blocksHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedBlocksHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const goalHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDescriptionSaveInFlightRef = useRef(false);

  const clearHoverTimeout = (timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleHoverHide = (
    timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
    hideFn: () => void,
  ) => {
    clearHoverTimeout(timeoutRef);
    timeoutRef.current = setTimeout(() => {
      hideFn();
      timeoutRef.current = null;
    }, HOVER_ICON_LINGER_MS);
  };

  const handleDescriptionMouseEnter = useCallback(() => {
    clearHoverTimeout(descriptionActionsHideTimeoutRef);
    clearHoverTimeout(newDayHoverTimeoutRef);
    setShowDescriptionActions(true);
  }, []);

  const handleDescriptionMouseLeave = useCallback(() => {
    scheduleHoverHide(descriptionActionsHideTimeoutRef, () => {
      setShowDescriptionActions(false);
    });
  }, [scheduleHoverHide]);

  const handleNewDayMouseEnter = useCallback(() => {
    clearHoverTimeout(descriptionActionsHideTimeoutRef);
    clearHoverTimeout(newDayHoverTimeoutRef);
    setShowDescriptionActions(true);
    setShowNewDayAction(true);
  }, []);

  const handleNewDayMouseLeave = useCallback(() => {
    scheduleHoverHide(newDayHoverTimeoutRef, () => {
      setShowDescriptionActions(false);
      setShowNewDayAction(false);
    });
  }, [scheduleHoverHide]);

  const handleNameMouseEnter = useCallback(() => {
    clearHoverTimeout(nameHoverTimeoutRef);
    setIsHoveringName(true);
  }, []);

  const handleNameMouseLeave = useCallback(() => {
    scheduleHoverHide(nameHoverTimeoutRef, () => {
      setIsHoveringName(false);
    });
  }, [scheduleHoverHide]);

  const handleBlocksMouseEnter = useCallback(() => {
    clearHoverTimeout(blocksHoverTimeoutRef);
    setIsHoveringBlocks(true);
  }, []);

  const handleBlocksMouseLeave = useCallback(() => {
    scheduleHoverHide(blocksHoverTimeoutRef, () => {
      setIsHoveringBlocks(false);
    });
  }, [scheduleHoverHide]);

  const handleCompletedBlocksMouseEnter = useCallback(() => {
    clearHoverTimeout(completedBlocksHoverTimeoutRef);
    setIsHoveringCompletedBlocks(true);
  }, []);

  const handleCompletedBlocksMouseLeave = useCallback(() => {
    scheduleHoverHide(completedBlocksHoverTimeoutRef, () => {
      setIsHoveringCompletedBlocks(false);
    });
  }, [scheduleHoverHide]);

  const handleGoalMouseEnter = useCallback(() => {
    clearHoverTimeout(goalHoverTimeoutRef);
    setIsHoveringGoal(true);
  }, []);

  const handleGoalMouseLeave = useCallback(() => {
    scheduleHoverHide(goalHoverTimeoutRef, () => {
      setIsHoveringGoal(false);
    });
  }, [scheduleHoverHide]);

  useEffect(() => {
    return () => {
      clearHoverTimeout(descriptionActionsHideTimeoutRef);
      clearHoverTimeout(newDayHoverTimeoutRef);
      clearHoverTimeout(nameHoverTimeoutRef);
      clearHoverTimeout(blocksHoverTimeoutRef);
      clearHoverTimeout(completedBlocksHoverTimeoutRef);
      clearHoverTimeout(goalHoverTimeoutRef);
    };
  }, []);

  useEffect(() => {
    // Reset hover-driven action visibility when modal context changes.
    clearHoverTimeout(descriptionActionsHideTimeoutRef);
    clearHoverTimeout(newDayHoverTimeoutRef);
    clearHoverTimeout(nameHoverTimeoutRef);
    clearHoverTimeout(blocksHoverTimeoutRef);
    clearHoverTimeout(completedBlocksHoverTimeoutRef);
    clearHoverTimeout(goalHoverTimeoutRef);
    setShowDescriptionActions(false);
    setShowNewDayAction(false);
    setIsHoveringName(false);
    setIsHoveringBlocks(false);
    setIsHoveringCompletedBlocks(false);
    setIsHoveringGoal(false);
    setIsEditingName(false);
    setNameText('');
    setNameSaveError(null);
  }, [open, habit?.habitId, selectedVersion]);
  
  // List of available goal units
  const availableGoalUnits = ['sessions', 'minutes', 'hours', 'count', 'accounting sessions', 'percent'];
  const availableIntervalUnits = ['day', 'week', 'month', 'year'];

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await habitService.getCategories();
        if (response.success && response.data) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch the block sequence for this specific habit (blockSequenceId === habitId convention).
  // Uses habitDefinition?.category (local state) rather than habit.category (prop) so that
  // a category change from Manage → something else triggers an immediate re-fetch without
  // waiting for the parent to pass down an updated habit prop.
  useEffect(() => {
    const effectiveCategory = habitDefinition?.category ?? habit?.category;
    if (!habit || effectiveCategory === 'Manage') return;
    const fetchBlocks = async () => {
      setBlocksLoading(true);
      setBlocksError(null);
      // Clear stale sequences immediately so the old category color is never shown
      // while the fetch is in flight.
      setBlockSequences([]);
      try {
        const res = await blockService.getAllBlockSequences();
        if (res.success) {
          const filtered = (res.data ?? []).filter(
            (seq) => seq.blockSequenceId === habit.habitId,
          );
          setBlockSequences(filtered);
        } else {
          console.error('Failed to fetch block sequences:', res.error);
          setBlocksError('Failed to load block sequences. Please try again later.');
        }
      } catch (error) {
        console.error('Error fetching block sequences:', error);
        setBlocksError('Failed to load block sequences. Please try again later.');
      } finally {
        setBlocksLoading(false);
      }
    };
    fetchBlocks();
  }, [habit, habitDefinition?.category]);

  const activeHabitName = habit?.habitDisplayName ?? habitDefinition?.habitDisplayName ?? undefined;

  useEffect(() => {
    if (!isChildHabit) {
      setResolvedParentHabitDisplayName(null);
      return;
    }

    if (currentParentHabitDisplayName) {
      setResolvedParentHabitDisplayName(currentParentHabitDisplayName);
      return;
    }

    const parentHabitId = habitDefinition?.parentHabitId ?? habit?.parentHabitId;
    if (!parentHabitId) {
      setResolvedParentHabitDisplayName(null);
      return;
    }

    let cancelled = false;

    const loadParentHabit = async () => {
      const response = await habitService.getHabit(parentHabitId);
      if (!cancelled && response.success && response.data) {
        setResolvedParentHabitDisplayName(response.data.habitDisplayName);
      }
    };

    void loadParentHabit();

    return () => {
      cancelled = true;
    };
  }, [currentParentHabitDisplayName, habit?.parentHabitId, habitDefinition?.parentHabitId, isChildHabit]);

  const getEffectiveParentGoal = React.useCallback((definition: HabitDefinition | null, children: HabitDefinition[] = []) => {
    if (!definition) return null;
    if (children.length === 0) return definition.goal;

    let totalThreshold = 0;
    let totalMin = 0;
    let totalMax = 0;
    let sawRangeChild = false;
    let sawAverageChild = false;

    for (const child of children) {
      const childGoal = child.goal;
      if (childGoal.type === 'RANGE') {
        sawRangeChild = true;
        totalMin += childGoal.min;
        totalMax += childGoal.max;
        continue;
      }
      if (childGoal.type === 'AVERAGE') {
        sawAverageChild = true;
        totalMin += childGoal.min;
        totalMax += childGoal.max;
        continue;
      }

      const childValue = childGoal.value;
      totalThreshold += childValue;
      totalMin += childValue;
      totalMax += childValue;
    }

    if (sawAverageChild) {
      return { type: 'AVERAGE' as const, min: totalMin, max: totalMax };
    }
    if (sawRangeChild) {
      return { type: 'RANGE' as const, min: totalMin, max: totalMax };
    }

    return { type: 'THRESHOLD' as const, value: totalThreshold };
  }, []);

  useEffect(() => {
    if (!open || !habit) {
      setGraphData(null);
      setGraphError(null);
      return;
    }

    const fetchGraph = async () => {
      setGraphLoading(true);
      setGraphError(null);
      try {
        const graphApi = (blockService as typeof blockService & { getBlockNodeGraph?: (habitName?: string) => Promise<BlockNodeGraphResponse> }).getBlockNodeGraph;
        if (typeof graphApi === 'function') {
          const res = await graphApi(activeHabitName);
          setGraphData(res);
          if (!res.success) {
            setGraphError(res.error ?? 'Failed to load block graph.');
          }
        } else {
          setGraphData({ success: true, nodes: [], edges: [] });
        }
      } catch (error) {
        console.error('Error fetching block graph:', error);
        setGraphError('Failed to load block graph. Please try again later.');
      } finally {
        setGraphLoading(false);
      }
    };

    fetchGraph();
  }, [open, habit, activeHabitName]);

  // Set the default selected version when versions are loaded
  useEffect(() => {
    if (habitVersions.length > 0) {
      console.log('HabitDetailModal - habitVersions:', habitVersions);
      // Find the latest published version or the first version if no published version exists
      const latestPublished = habitVersions.find(v => v.isLatestPublishedVersion);
      console.log('HabitDetailModal - latestPublished:', latestPublished);
      const defaultVersion = latestPublished || habitVersions[0];
      setSelectedVersion(defaultVersion.version);
      setHabitDefinition(defaultVersion.habitDefinition);
    }
  }, [habitVersions]);

  // Update lastModifiedAt when habitDefinition changes
  useEffect(() => {
    if (habitDefinition?.lastModifiedAt) {
      setLastModifiedAt(habitDefinition.lastModifiedAt);
    }
    if (habitDefinition?.category) {
      setSelectedCategory(habitDefinition.category);
    }
    if (habitDefinition?.goal?.type) {
      setSelectedGoalType(habitDefinition.goal.type as 'RANGE' | 'THRESHOLD' | 'AVERAGE');
    }
    if (habitDefinition?.goalUnit) {
      setGoalUnit(habitDefinition.goalUnit);
    }
    if (habitDefinition?.intervalUnit) {
      setIntervalUnit(habitDefinition.intervalUnit);
    }
    if (habitDefinition?.goal) {
      const goalForDisplay = getEffectiveParentGoal(habitDefinition, childHabitDefinitions) ?? habitDefinition.goal;
      setSelectedGoalType(goalForDisplay.type as 'RANGE' | 'THRESHOLD' | 'AVERAGE');
      if (goalForDisplay.type === 'THRESHOLD') {
        setThresholdValue(goalForDisplay.value.toString());
      } else if ('min' in goalForDisplay && 'max' in goalForDisplay) {
        setRangeMin(String(goalForDisplay.min ?? ''));
        setRangeMax(String(goalForDisplay.max ?? ''));
      }
    }
  }, [habitDefinition, childHabitDefinitions, getEffectiveParentGoal]);

  useEffect(() => {
    if (!open) {
      setActiveChildHabit(null);
    }
  }, [open]);

  useEffect(() => {
    if (isChildHabit) {
      setChildHabitDefinitions([]);
      setChildHabitProgressById({});
      return;
    }

    let cancelled = false;

    const loadChildHabits = async () => {
      const childIds = habitDefinition?.childHabitIds || [];
      if (childIds.length === 0) {
        setChildHabitDefinitions([]);
        return;
      }

      const childResponses = await Promise.all(
        childIds.map(async (childId) => {
          const response = await habitService.getHabit(childId);
          return response.success && response.data ? response.data : null;
        })
      );

      if (!cancelled) {
        setChildHabitDefinitions(childResponses.filter((child): child is HabitDefinition => child !== null));
      }
    };

    void loadChildHabits();

    return () => {
      cancelled = true;
    };
  }, [habitDefinition?.childHabitIds, isChildHabit]);

  useEffect(() => {
    if (!open || isChildHabit || !habit) {
      setChildHabitProgressById({});
      return;
    }

    const childIds = habitDefinition?.childHabitIds || [];
    if (childIds.length === 0) {
      setChildHabitProgressById({});
      return;
    }

    let cancelled = false;

    const loadChildProgress = async () => {
      const reportResponse = await habitService.getReport({ includeChildren: true });
      if (!reportResponse.success || !reportResponse.data || cancelled) {
        return;
      }

      const relevantIds = new Set(childIds);
      const progressById = reportResponse.data.habits.reduce<Record<string, HabitSummary>>((acc, summary) => {
        if (relevantIds.has(summary.habitId)) {
          acc[summary.habitId] = summary;
        }
        return acc;
      }, {});

      if (!cancelled) {
        setChildHabitProgressById(progressById);
      }
    };

    void loadChildProgress();

    return () => {
      cancelled = true;
    };
  }, [open, isChildHabit, habit, habitDefinition?.childHabitIds]);

  const handleContextMenu = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    longPressOpenedAtRef.current = Date.now();
    // Close any open menu first, then set the new position
    setContextMenuPosition(null);
    
    let clientX: number, clientY: number;
    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    
    // Use setTimeout so the close renders before reopening at the new position
    setTimeout(() => {
      setContextMenuPosition({ mouseX: clientX + 2, mouseY: clientY - 6 });
    }, 0);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    if (event.touches.length !== 1) {
      touchStartRef.current = null;
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      return;
    }

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    const touch = event.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };

    longPressTimerRef.current = setTimeout(() => {
      longPressOpenedAtRef.current = Date.now();
      setContextMenuPosition(null);
      setContextMenuPosition({ mouseX: touch.clientX + 2, mouseY: touch.clientY - 6 });
      longPressTimerRef.current = null;
    }, LONG_PRESS_DURATION_MS);
  };

  const handleTouchEnd = () => {
    // Clear the timer on touch end
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (!touchStartRef.current || event.touches.length !== 1) {
      touchStartRef.current = null;
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    if (deltaX <= TOUCH_MOVE_CANCEL_THRESHOLD_PX && deltaY <= TOUCH_MOVE_CANCEL_THRESHOLD_PX) {
      return;
    }

    // Clear the timer if finger moves enough to indicate a scroll/drag gesture.
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  };

  const handleCloseContextMenu = (_event?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' && Date.now() - longPressOpenedAtRef.current < LONG_PRESS_CLOSE_GUARD_MS) {
      return;
    }
    setContextMenuPosition(null);
  };

  const handleCreateNewVersionClick = async () => {
    setContextMenuPosition(null);
    // Client-side fast path: if we already know the latest version is a draft, show error immediately
    if (habitVersions.length > 0 && !habitVersions[0].isLatestPublishedVersion) {
      setVersionSnackbarSeverity('warning');
      setVersionCreationError('To create a new habit version, publish the latest version in draft.');
      return;
    }
    if (!habit) return;
    // Compute the new version label before calling the API (e.g. "V0000001" → V2)
    const currentVersionNum = habitVersions.length > 0
      ? parseInt(habitVersions[0].version.slice(1), 10)
      : 0;
    const newVersionLabel = `V${currentVersionNum + 1}`;
    setIsCreatingVersion(true);
    setVersionCreationError(null);
    const response: CreateNewVersionResponse = await habitService.createNewVersion(habit.habitId);
    setIsCreatingVersion(false);
    if (!response.success) {
      setVersionSnackbarSeverity('warning');
      setVersionCreationError('To create a new habit version, publish the latest version in draft.');
      if (response.code === 'LATEST_VERSION_IS_DRAFT') {
        // Re-sync stale UI data
        onHabitUpdated?.();
      }
      return;
    }
    setVersionSnackbarSeverity('success');
    setVersionCreationError(<>Created "{habit.habitDisplayName}" <strong>{newVersionLabel}</strong> in draft.</>);
    onHabitUpdated?.();
  };

  const handleCreateChildHabitClick = () => {
    setContextMenuPosition(null);
    setIsChildHabitCreateOpen(true);
  };

  const handleOpenChildHabit = useCallback(async (childHabit: HabitDefinition) => {
    const versionsResponse = await habitService.getHabitVersions(childHabit.habitId);
    const versions = versionsResponse.success && versionsResponse.data ? versionsResponse.data : [];
    const currentParentDisplayName = localHabitName ?? habitDefinition?.habitDisplayName ?? habit?.habitDisplayName ?? '';

    const childProgress = childHabitProgressById[childHabit.habitId];
    const goalAmount = childHabit.goal.type === 'THRESHOLD' ? childHabit.goal.value : childHabit.goal.max;

    setActiveChildHabit({
      habit: childProgress || {
        habitId: childHabit.habitId,
        habitDisplayName: formatChildHabitDisplayName(childHabit.habitDisplayName, currentParentDisplayName),
        goalAmountScaled: goalAmount,
        goalAmount: goalAmount,
        goalUnit: childHabit.goalUnit,
        currentAmountCompletedOverReportTimeInterval: 0,
        currentAmountCompletedOverGoalTimeInterval: 0,
        percentageCompletedOverReportTimeInterval: 0,
        percentageCompletedOverGoalTimeInterval: 0,
        category: childHabit.category,
        parentHabitId: childHabit.parentHabitId,
        entries: [],
      },
      habitVersions: versions,
      parentHabitDisplayName: currentParentDisplayName,
    });

    if (onOpenHabitById) {
      onOpenHabitById(childHabit.habitId);
    }
  }, [childHabitProgressById, habit?.habitDisplayName, habitDefinition?.habitDisplayName, localHabitName, onOpenHabitById]);

  const handleDeleteClick = () => {
    setContextMenuPosition(null);
    setDeleteError(null);
    setDeleteConfirmText('');
    setDeleteStep(1);
    setDeleteConfirmOpen(true);
  };

  const handlePublishVersionClick = async () => {
    setContextMenuPosition(null);
    if (!habit) return;
    // Find the selected version info to get its lastModifiedAt
    const versionInfo = habitVersions.find(v => v.version === selectedVersion);
    if (!versionInfo || versionInfo.isLatestPublishedVersion) return;
    const lastModifiedAt = versionInfo.habitDefinition.lastModifiedAt;
    if (!lastModifiedAt) {
      setVersionSnackbarSeverity('error');
      setVersionCreationError('Cannot publish: missing last modified timestamp. Please close and reopen the modal.');
      return;
    }
    setIsPublishingVersion(true);
    setVersionCreationError(null);
    const response: PublishHabitVersionResponse = await habitService.publishHabitVersion(habit.habitId, {
      versionSK: selectedVersion,
      lastModifiedAt,
    });
    setIsPublishingVersion(false);
    if (!response.success) {
      if (response.code === 'CONFLICT') {
        setVersionSnackbarSeverity('warning');
        setVersionCreationError('This version was modified since you opened the modal. Please close and reopen to retry.');
        onHabitUpdated?.();
      } else {
        setVersionSnackbarSeverity('error');
        setVersionCreationError(response.error || 'Failed to publish version.');
      }
      return;
    }
    setVersionSnackbarSeverity('success');
    const prettyVersion = `V${parseInt(selectedVersion.slice(1), 10)}`;
    setVersionCreationError(<>Published "<strong>{habit.habitDisplayName}</strong>" {prettyVersion}.</>);
    onHabitUpdated?.();
  };

  const handleDeleteCancel = () => {
    if (isDeleting) return;
    setDeleteConfirmOpen(false);
    setDeleteError(null);
    setDeleteConfirmText('');
    setDeleteStep(1);
  };

  const handleDeleteConfirm = async () => {
    if (!habit) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const response = await habitService.deleteHabit(habit.habitId);
      if (!response.success) {
        setDeleteError(response.error || 'Failed to delete habit');
        setIsDeleting(false);
        return;
      }
      setDeleteConfirmOpen(false);
      onClose();
      if (onHabitDeleted) {
        onHabitDeleted();
      }
    } catch (error) {
      setDeleteError('Failed to delete habit. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleManageCategoryCancel = () => {
    if (isManageCategorySaving) return;
    setManageCategoryConfirmOpen(false);
    setManageCategoryConfirmText('');
    setManageCategoryConfirmStep(1);
    setManageCategoryError(null);
    setPendingCategoryRequest(null);
    // Keep isEditingDescription open so the user can revise the category choice.
  };

  const handleManageCategoryConfirm = useCallback(async () => {
    if (!habit || !pendingCategoryRequest) return;
    setIsManageCategorySaving(true);
    setManageCategoryError(null);
    try {
      const response = await habitService.updateCategory(habit.habitId, {
        category: selectedCategory,
        versionSK: pendingCategoryRequest.versionSK,
        lastModifiedAt: pendingCategoryRequest.lastModifiedAt,
        isPublished: pendingCategoryRequest.isPublished,
      });
      if (!response.success) {
        setManageCategoryError(response.error || 'Failed to update category');
        return;
      }
      if (response.data) {
        setHabitDefinition(response.data);
        setLastModifiedAt(response.data.lastModifiedAt || pendingCategoryRequest.lastModifiedAt);
      }
      setManageCategoryConfirmOpen(false);
      setManageCategoryConfirmText('');
      setManageCategoryConfirmStep(1);
      setPendingCategoryRequest(null);
      setIsEditingDescription(false);
      setDescriptionText('');
      setCategoryAnchorEl(null);
      onHabitUpdated?.();
      if (onHabitReloadRequired) {
        onHabitReloadRequired();
      } else {
        onClose();
      }
    } catch {
      setManageCategoryError('Failed to update category. Please try again.');
    } finally {
      setIsManageCategorySaving(false);
    }
  }, [habit, pendingCategoryRequest, selectedCategory, onHabitUpdated, onHabitReloadRequired, onClose]);

  // Check if current version is editable (latest published or draft)
  const isVersionEditable = useCallback((): boolean => {
    const currentVersionInfo = habitVersions.find(v => v.version === selectedVersion);
    if (!currentVersionInfo) return false;
    
    // Editable if it's the latest published version
    if (currentVersionInfo.isLatestPublishedVersion) return true;
    
    // Editable if it's a draft (first version and not published)
    const isDraft = habitVersions[0]?.version === selectedVersion && !currentVersionInfo.isLatestPublishedVersion;
    return isDraft;
  }, [habitVersions, selectedVersion]);

  const canEditGoal = !isChildHabit;

  const handleStartEditGoal = () => {
    if (!canEditGoal) return;
    setIsEditingGoal(true);
    setGoalError('');
    // Initialize child goal amounts from fetched child definitions
    if (childHabitDefinitions.length > 0) {
      const amounts: Record<string, { threshold?: string; min?: string; max?: string }> = {};
      for (const child of childHabitDefinitions) {
        if (child.goal?.type === 'THRESHOLD') {
          amounts[child.habitId] = { threshold: String(child.goal.value ?? '') };
        } else {
          amounts[child.habitId] = { min: String(child.goal.min ?? ''), max: String(child.goal.max ?? '') };
        }
      }
      setChildGoalAmounts(amounts);
    }
  };

  const handleCancelEditGoal = () => {
    setIsEditingGoal(false);
    setGoalError('');
    // Reset to the derived parent goal when a habit has children; otherwise use the stored value.
    if (habitDefinition) {
      const goalForDisplay = getEffectiveParentGoal(habitDefinition, childHabitDefinitions) ?? habitDefinition.goal;
      setGoalUnit(habitDefinition.goalUnit);
      setIntervalUnit(habitDefinition.intervalUnit);
      setSelectedGoalType(goalForDisplay.type as 'RANGE' | 'THRESHOLD' | 'AVERAGE');
      if (goalForDisplay.type === 'THRESHOLD') {
        setThresholdValue(goalForDisplay.value.toString());
      } else if ('min' in goalForDisplay && 'max' in goalForDisplay) {
        setRangeMin(String(goalForDisplay.min ?? ''));
        setRangeMax(String(goalForDisplay.max ?? ''));
      }
    }
  };

  // Parent goal amounts are always derived from child goal amounts when children exist.
  const isParentGoalAutoCalculated = isEditingGoal && childHabitDefinitions.length > 0;

  useEffect(() => {
    if (!isParentGoalAutoCalculated) return;
    const sortedChildren = [...childHabitDefinitions].sort((a, b) =>
      a.habitDisplayName.localeCompare(b.habitDisplayName)
    );
    if (selectedGoalType === 'THRESHOLD') {
      const total = sortedChildren.reduce((sum, child) => {
        const amt = childGoalAmounts[child.habitId];
        return sum + (parseFloat(amt?.threshold || '0') || 0);
      }, 0);
      setThresholdValue(String(total));
    } else {
      const totalMin = sortedChildren.reduce((sum, child) => {
        const amt = childGoalAmounts[child.habitId];
        return sum + (parseFloat(amt?.min || '0') || 0);
      }, 0);
      const totalMax = sortedChildren.reduce((sum, child) => {
        const amt = childGoalAmounts[child.habitId];
        return sum + (parseFloat(amt?.max || '0') || 0);
      }, 0);
      setRangeMin(String(totalMin));
      setRangeMax(String(totalMax));
    }
  }, [childGoalAmounts, selectedGoalType, childHabitDefinitions, isParentGoalAutoCalculated]);

  const handleSaveGoal = useCallback(async () => {
    if (!habit || !habitDefinition || !canEditGoal) return;

    // Validate inputs inline
    const validateInputs = (): { isValid: boolean; error: string } => {
      // Check goal unit
      if (!goalUnit || goalUnit.trim() === '') {
        return { isValid: false, error: 'Goal unit is required' };
      }

      // Check interval unit for non-average goals.
      if (selectedGoalType !== 'AVERAGE' && (!intervalUnit || intervalUnit.trim() === '')) {
        return { isValid: false, error: 'Interval unit is required' };
      }

      if (selectedGoalType === 'THRESHOLD') {
        // Validate threshold value
        if (!thresholdValue || thresholdValue.trim() === '') {
          return { isValid: false, error: 'Threshold value is required' };
        }
        const value = parseFloat(thresholdValue);
        if (isNaN(value)) {
          return { isValid: false, error: 'Threshold value must be a valid number' };
        }
        if (value <= 0) {
          return { isValid: false, error: 'Threshold value must be greater than 0' };
        }
      } else {
        // Validate range values
        if (!rangeMin || rangeMin.trim() === '' || !rangeMax || rangeMax.trim() === '') {
          return { isValid: false, error: 'Both minimum and maximum values are required' };
        }
        const min = parseFloat(rangeMin);
        const max = parseFloat(rangeMax);
        if (isNaN(min) || isNaN(max)) {
          return { isValid: false, error: `${selectedGoalType === 'AVERAGE' ? 'Average' : 'Range'} values must be valid numbers` };
        }
        if (selectedGoalType === 'RANGE' && (min < 0 || max < 0)) {
          return { isValid: false, error: 'Goal amounts cannot be negative' };
        }
        if (min > max) {
          return { isValid: false, error: 'Minimum value cannot be greater than maximum value' };
        }
      }

      return { isValid: true, error: '' };
    };

    // Validate inputs
    const validation = validateInputs();
    if (!validation.isValid) {
      setGoalError(validation.error);
      return;
    }

    setIsSaving(true);
    setGoalError('');

    try {
      const currentVersionInfo = habitVersions.find(v => v.version === selectedVersion);
      if (!currentVersionInfo) {
        setGoalError('Version information not found');
        setIsSaving(false);
        return;
      }

      const effectiveIntervalUnit = intervalUnit || habitDefinition?.intervalUnit || 'week';

      const request: UpdateHabitGoalRequest = {
        goalType: selectedGoalType,
        goalUnit: goalUnit,
        intervalUnit: effectiveIntervalUnit,
        versionSK: selectedVersion,
        lastModifiedAt: lastModifiedAt,
        isPublished: currentVersionInfo.isLatestPublishedVersion,
      };

      if (selectedGoalType === 'THRESHOLD') {
        request.goalValue = parseFloat(thresholdValue);
      } else {
        request.goalMin = parseFloat(rangeMin);
        request.goalMax = parseFloat(rangeMax);
      }

      const response = await habitService.updateGoal(habit.habitId, request);

      if (!response.success) {
        const error = new Error(response.error || 'Failed to save goal changes');
        (error as Error & { response?: { data: typeof response } }).response = { data: response };
        throw error;
      }

      // Also save changed child goal amounts
      for (const child of childHabitDefinitions) {
        const editedAmounts = childGoalAmounts[child.habitId];
        if (!editedAmounts) continue;

        const originalGoal = child.goal;
        const amountChanged =
          (selectedGoalType === 'THRESHOLD' && editedAmounts.threshold !== undefined &&
            editedAmounts.threshold !== String(originalGoal?.type === 'THRESHOLD' ? originalGoal.value : '')) ||
          (selectedGoalType !== 'THRESHOLD' &&
            (editedAmounts.min !== String(originalGoal?.type !== 'THRESHOLD' ? originalGoal.min : '') ||
             editedAmounts.max !== String(originalGoal?.type !== 'THRESHOLD' ? originalGoal.max : '')));

        if (amountChanged) {
          try {
            // Fetch child versions to get versionSK
            const childVersionsResponse = await habitService.getHabitVersions(child.habitId);
            if (childVersionsResponse.success && childVersionsResponse.data) {
              const latestVersion = childVersionsResponse.data.find(v => v.isLatestPublishedVersion) || childVersionsResponse.data[0];
              if (latestVersion) {
                const childRequest: UpdateHabitGoalRequest = {
                  goalType: selectedGoalType,
                  goalUnit,
                  intervalUnit: effectiveIntervalUnit,
                  versionSK: latestVersion.version,
                  lastModifiedAt: child.lastModifiedAt || latestVersion.habitDefinition.lastModifiedAt || '',
                  isPublished: latestVersion.isLatestPublishedVersion,
                };
                if (selectedGoalType === 'THRESHOLD') {
                  childRequest.goalValue = parseFloat(editedAmounts.threshold || '0');
                } else {
                  childRequest.goalMin = parseFloat(editedAmounts.min || '0');
                  childRequest.goalMax = parseFloat(editedAmounts.max || '0');
                }
                await habitService.updateGoal(child.habitId, childRequest);
              }
            }
          } catch (childErr) {
            console.error(`Failed to update child goal for ${child.habitId}:`, childErr);
          }
        }
      }

      // Update local state with fresh data
      if (response.data) {
        setHabitDefinition(response.data);
        if (response.data.lastModifiedAt) {
          setLastModifiedAt(response.data.lastModifiedAt);
        }
      }

      setIsEditingGoal(false);
      
      // Notify parent to refresh
      if (onHabitUpdated) {
        onHabitUpdated();
      }
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      if (errorResponse?.code === 'CONFLICT') {
        setGoalError('This habit was modified by another user. Please refresh and try again.');
      } else {
        setGoalError(errorResponse?.error || 'Failed to save goal changes');
      }
    } finally {
      setIsSaving(false);
    }
  }, [habit, habitDefinition, habitVersions, selectedVersion, lastModifiedAt, goalUnit, intervalUnit, selectedGoalType, thresholdValue, rangeMin, rangeMax, childHabitDefinitions, childGoalAmounts, onHabitUpdated, canEditGoal]);

  const handleStartEdit = () => {
    setDescriptionText(habitDefinition?.description || '');
    setSelectedCategory(habitDefinition?.category || habit?.category || '');
    setIsEditingDescription(true);
    setSaveError(null);
  };

  const handleStartEditName = () => {
    const currentHabitName = habitName;

    if (isChildHabit && lockedChildNamePrefix && currentHabitName.startsWith(lockedChildNamePrefix)) {
      setNameText(currentHabitName.slice(lockedChildNamePrefix.length));
    } else {
      setNameText(isChildHabit ? extractChildHabitNameSuffix(currentHabitName, resolvedParentHabitDisplayName) : currentHabitName);
    }
    setNameSaveError(null);
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setNameText('');
    setNameSaveError(null);
  };

  const handleSaveName = useCallback(async () => {
    if (!habit) return;
    const trimmed = nameText.trim();
    if (!trimmed) {
      setNameSaveError('Habit name cannot be empty.');
      return;
    }
    const lockedPrefix = isChildHabit
      ? resolvedParentHabitDisplayName
        ? `${resolvedParentHabitDisplayName}${CHILD_HABIT_NAME_SEPARATOR}`
        : ''
      : '';
    const finalName = lockedPrefix ? `${lockedPrefix}${trimmed}` : trimmed;
    setIsNameSaving(true);
    setNameSaveError(null);
    try {
      const response = await habitService.updateName(habit.habitId, { name: finalName } as UpdateHabitNameRequest);
      if (!response.success) {
        setNameSaveError(response.error || 'Failed to save name.');
        return;
      }
      setLocalHabitName(finalName);
      setIsEditingName(false);
      setNameText('');
      onHabitUpdated?.();
    } catch {
      setNameSaveError('Failed to save name. Please try again.');
    } finally {
      setIsNameSaving(false);
    }
  }, [habit, isChildHabit, nameText, onHabitUpdated, resolvedParentHabitDisplayName]);

  const handleCancelEdit = () => {
    setIsEditingDescription(false);
    setDescriptionText('');
    setSelectedCategory(habitDefinition?.category || habit?.category || '');
    setCategoryAnchorEl(null);
    setSaveError(null);
  };

  const handleCategoryClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isEditingDescription) {
      setCategoryAnchorEl(event.currentTarget);
    }
  };

  const handleCategorySelect = (category: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setSelectedCategory(category);
    setCategoryAnchorEl(null);
  };

  const handleCategoryClose = () => {
    setCategoryAnchorEl(null);
  };

  const isDoDayTasksHabit = habit?.habitId === DO_DAY_TASKS_HABIT_ID;

  const handleCheckboxToggle = useCallback(async (lineNumber: number) => {
    if (!habit || !habitDefinition || !isVersionEditable()) return;

    const description = habitDefinition.description;
    const lines = description.split('\n');
    
    // Toggle the checkbox at the specific line number
    const line = lines[lineNumber];
    const checkboxMatch = line.match(/^(\s*[-*+]\s+)\[([ xX])\](.*)$/);
    
    if (!checkboxMatch) return;
    
    const [, prefix, checked, suffix] = checkboxMatch;
    const newChecked = checked === ' ' ? 'x' : ' ';
    lines[lineNumber] = `${prefix}[${newChecked}]${suffix}`;
    
    const updatedDescription = lines.join('\n');
    const previousDefinition = habitDefinition;

    // Optimistic update - update UI immediately
    setHabitDefinition({
      ...habitDefinition,
      description: updatedDescription
    });

    // Save the updated description in the background
    try {
      const currentVersionInfo = habitVersions.find(v => v.version === selectedVersion);
      if (!currentVersionInfo) return;

      const response = await habitService.updateDescription(habit.habitId, {
        description: updatedDescription,
        versionSK: selectedVersion,
        lastModifiedAt: habitDefinition.lastModifiedAt || '',
        isPublished: currentVersionInfo.isLatestPublishedVersion,
      });

      if (response.success && response.data) {
        // Update with the response from server (includes new lastModifiedAt)
        setHabitDefinition(response.data);
        setLastModifiedAt(response.data.lastModifiedAt || previousDefinition.lastModifiedAt || '');
        if (onHabitUpdated) {
          onHabitUpdated();
        }
      } else {
        // Revert on error
        setHabitDefinition(previousDefinition);
        if (response.code === 'CONFLICT') {
          setSaveError('Unable to update checkbox: This habit was modified by another user or session. Please refresh and try again.');
          if (onHabitUpdated) {
            onHabitUpdated();
          }
        } else {
          setSaveError(response.error || 'Failed to update checkbox. Please try again.');
        }
      }
    } catch (error) {
      console.error('Failed to toggle checkbox:', error);
      // Revert on error
      setHabitDefinition(previousDefinition);
      setSaveError('Failed to update checkbox. Please try again.');
    }
  }, [habit, habitDefinition, habitVersions, selectedVersion, onHabitUpdated, isVersionEditable]);

  const handleSaveDescription = useCallback(async () => {
    if (!habit || !habitDefinition) return;
    if (isDescriptionSaveInFlightRef.current) return;

    isDescriptionSaveInFlightRef.current = true;

    setIsSaving(true);
    setSaveError(null);

    try {
      const currentVersionInfo = habitVersions.find(v => v.version === selectedVersion);
      if (!currentVersionInfo) {
        setSaveError('Version information not found');
        setIsSaving(false);
        return;
      }

      const descriptionChanged = descriptionText !== habitDefinition.description;
      const categoryChanged = selectedCategory !== (habitDefinition.category || habit.category);
      const currentCat = habitDefinition.category || habit.category;
      const isToManage = selectedCategory === 'Manage';
      const isFromManage = currentCat === 'Manage';

      let updatedLastModifiedAt = lastModifiedAt;

      // Save description if changed
      if (descriptionChanged) {
        const response = await habitService.updateDescription(habit.habitId, {
          description: descriptionText,
          versionSK: selectedVersion,
          lastModifiedAt: updatedLastModifiedAt,
          isPublished: currentVersionInfo.isLatestPublishedVersion,
        });

        if (!response.success) {
          const error = new Error(response.error || 'Failed to update description');
          (error as Error & { response?: { data: typeof response } }).response = { data: response };
          throw error;
        }

        if (response.data) {
          setHabitDefinition(response.data);
          updatedLastModifiedAt = response.data.lastModifiedAt || updatedLastModifiedAt;
        }
      }

      // Save category if changed (using the updated lastModifiedAt if description was saved)
      if (categoryChanged) {
        if (isToManage && !isFromManage) {
          // Fresh server-side check: is the block sequence non-empty?
          const headBlockRes = await blockService.getBlockSequenceHeadBlock(habit.habitId);
          if (!headBlockRes.success) {
            const error = new Error(headBlockRes.error || 'Failed to confirm block sequence status');
            (error as Error & { response?: { data: typeof headBlockRes } }).response = { data: headBlockRes };
            throw error;
          }
          if (headBlockRes.headBlock !== 'None') {
            // Sequence has blocks — require explicit confirmation before deleting.
            setPendingCategoryRequest({
              lastModifiedAt: updatedLastModifiedAt,
              versionSK: selectedVersion,
              isPublished: currentVersionInfo.isLatestPublishedVersion,
            });
            setManageCategoryConfirmStep(1);
            setManageCategoryConfirmText('');
            setManageCategoryError(null);
            setManageCategoryConfirmOpen(true);
            // Return here; finally will clear isSaving. The confirmation modal
            // will complete the category update when the user confirms.
            return;
          }
          // Sequence is empty — proceed directly, no confirmation needed.
        }

        const response = await habitService.updateCategory(habit.habitId, {
          category: selectedCategory,
          versionSK: selectedVersion,
          lastModifiedAt: updatedLastModifiedAt,
          isPublished: currentVersionInfo.isLatestPublishedVersion,
        });

        if (!response.success) {
          const error = new Error(response.error || 'Failed to update category');
          (error as Error & { response?: { data: typeof response } }).response = { data: response };
          throw error;
        }

        if (response.data) {
          // For non-Manage→non-Manage: clear stale sequences in the same React
          // render batch as the new habitDefinition so the old color is never shown.
          if (categoryChanged && !isToManage && !isFromManage) {
            setBlockSequences([]);
          }
          setHabitDefinition(response.data);
          updatedLastModifiedAt = response.data.lastModifiedAt || updatedLastModifiedAt;
        }
      }

      setLastModifiedAt(updatedLastModifiedAt);
      setIsEditingDescription(false);
      setDescriptionText('');
      setCategoryAnchorEl(null);

      // Call parent callback to refresh habit data
      if (onHabitUpdated) {
        onHabitUpdated();
      }

      // If the category crossed the Manage boundary, trigger a full modal reload
      // so the Blocks section correctly appears or disappears with the skeleton.
      if (categoryChanged && (isToManage || isFromManage)) {
        if (onHabitReloadRequired) {
          onHabitReloadRequired();
        } else {
          onClose();
        }
      }
    } catch (error: any) {
      if (error.response?.data?.code === 'CONFLICT') {
        setSaveError('Unable to save: This habit was modified by another user or session. Please refresh and try again.');
      } else {
        setSaveError(error.response?.data?.error || 'Failed to save changes. Please try again.');
      }
    } finally {
      isDescriptionSaveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [habit, habitDefinition, habitVersions, selectedVersion, descriptionText, selectedCategory, lastModifiedAt, onHabitUpdated, onHabitReloadRequired, onClose]);

  const handleDescriptionFieldBlur = useCallback((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!isEditingDescription) {
      return;
    }

    const nextFocusedElement = event.relatedTarget as Node | null;
    if (nextFocusedElement && editFormRef.current?.contains(nextFocusedElement)) {
      return;
    }

    // On mobile, keyboard "Done" often blurs the textarea without a click event.
    // Defer save until the next frame to avoid racing viewport resize work.
    window.requestAnimationFrame(() => {
      void handleSaveDescription();
    });
  }, [isEditingDescription, handleSaveDescription]);

  const handleCreateNewDay = useCallback(async () => {
    if (!habit || !habitDefinition || !isVersionEditable()) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const currentVersionInfo = habitVersions.find(v => v.version === selectedVersion);
      if (!currentVersionInfo) {
        setSaveError('Version information not found');
        setIsSaving(false);
        return;
      }

      const response = await habitService.updateDescription(habit.habitId, {
        description: habitDefinition.description,
        versionSK: selectedVersion,
        lastModifiedAt: habitDefinition.lastModifiedAt || lastModifiedAt,
        isPublished: currentVersionInfo.isLatestPublishedVersion,
        resetCheckboxes: true,
      });

      if (!response.success) {
        const error = new Error(response.error || 'Failed to create new day');
        (error as Error & { response?: { data: typeof response } }).response = { data: response };
        throw error;
      }

      if (response.data) {
        setHabitDefinition(response.data);
        setLastModifiedAt(response.data.lastModifiedAt || lastModifiedAt);
        if (onHabitUpdated) {
          onHabitUpdated();
        }
      }
    } catch (error: any) {
      if (error.response?.data?.code === 'CONFLICT') {
        setSaveError('Unable to create new day: This habit was modified by another user or session. Please refresh and try again.');
        if (onHabitUpdated) {
          onHabitUpdated();
        }
      } else {
        setSaveError(error.response?.data?.error || 'Failed to create new day. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [habit, habitDefinition, habitVersions, selectedVersion, lastModifiedAt, onHabitUpdated, isVersionEditable]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Handle click outside edit form
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (!isEditingDescription) return;
      if (!descriptionContainerRef.current) return;
      if (descriptionContainerRef.current.contains(target)) return;
      if (manageCategoryConfirmOpen || deleteConfirmOpen) return;
      
      // Don't save if clicking inside the category dropdown menu when it's open
      if (categoryAnchorEl) {
        const popover = document.querySelector('[role="presentation"]');
        if (popover && popover.contains(target)) return;
      }
      
      handleSaveDescription();
    };

    if (isEditingDescription) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingDescription, categoryAnchorEl, manageCategoryConfirmOpen, deleteConfirmOpen, handleSaveDescription]);

  // Handle click outside blocks section to exit edit mode
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!isEditingBlocks) return;
      if (!blocksContainerRef.current) return;
      if (blocksContainerRef.current.contains(target)) return;
      // Ignore clicks that land inside a MUI Menu/Popover/Dialog portal (e.g. right-click context menu, confirmation dialog)
      if ((target as Element).closest?.('.MuiPopover-root, .MuiMenu-root, [role="menu"], .MuiDialog-root')) return;
      // Defer so that the textarea's onBlur fires first (blur precedes mouseup but not mousedown)
      setTimeout(() => setIsEditingBlocks(false), 0);
    };

    if (isEditingBlocks) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingBlocks]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!isEditingCompletedBlocks) return;
      if (!completedBlocksContainerRef.current) return;
      if (completedBlocksContainerRef.current.contains(target)) return;
      if ((target as Element).closest?.('.MuiPopover-root, .MuiMenu-root, [role="menu"], .MuiDialog-root')) return;
      setTimeout(() => setIsEditingCompletedBlocks(false), 0);
    };

    if (isEditingCompletedBlocks) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingCompletedBlocks]);

  const handleVersionChange = (event: SelectChangeEvent<string>) => {
    const version = event.target.value;
    setSelectedVersion(version);
    const versionInfo = habitVersions.find(v => v.version === version);
    if (versionInfo) {
      setHabitDefinition(versionInfo.habitDefinition);
    }
  };

  const getVersionLabel = (versionInfo: HabitVersionInfo) => {
    const versionNumber = versionInfo.version.replace('V', '').replace(/^0+/, '');
    return `V${versionNumber}`;
  };

  const hasLatestPublishedChip = (versionInfo: HabitVersionInfo) => {
    return versionInfo.isLatestPublishedVersion;
  };

  const hasInDraftChip = (versionInfo: HabitVersionInfo) => {
    // Show "IN DRAFT" if it's the latest version (first in array) and not published
    return habitVersions[0]?.version === versionInfo.version && !versionInfo.isLatestPublishedVersion;
  };

  const rawHabitName = localHabitName ?? habit?.habitDisplayName ?? '';
  const habitName = React.useMemo(() => {
    if (!isChildHabit) return rawHabitName;
    return formatChildHabitDisplayName(rawHabitName, resolvedParentHabitDisplayName);
  }, [isChildHabit, rawHabitName, resolvedParentHabitDisplayName]);
  const lockedChildNamePrefix = React.useMemo(() => {
    if (!isChildHabit) return '';
    if (resolvedParentHabitDisplayName) {
      return `${resolvedParentHabitDisplayName}${CHILD_HABIT_NAME_SEPARATOR}`;
    }
    return '';
  }, [isChildHabit, resolvedParentHabitDisplayName]);

  if (!habit) return null;

  // Helper to format numbers - show whole numbers without decimals
  const formatNumber = (num?: number | null) => {
    if (typeof num !== 'number' || !Number.isFinite(num)) return '0';
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
  };

  const formatAmount = (num?: number | null) => {
    const formatted = formatNumber(num);
    return typeof num === 'number' && Number.isFinite(num) && num < 0 ? `(${formatted})` : formatted;
  };

  const getProgressColor = (percentage: number): 'success' | 'warning' | 'error' => {
    if (percentage >= 100) return 'success';
    if (percentage >= 80) return 'warning';
    return 'error';
  };

  const effectiveParentGoal = getEffectiveParentGoal(habitDefinition, childHabitDefinitions);

  const formatProgressAmount = (habitSummary: HabitSummary, definition: HabitDefinition | null) => {
    if (!definition) {
      return `${formatAmount(habitSummary.currentAmountCompletedOverGoalTimeInterval)} / ${formatAmount(habitSummary.goalAmount)} ${habitSummary.goalUnit}`;
    }

    const effectiveGoal = definition.childHabitIds?.length ? getEffectiveParentGoal(definition, childHabitDefinitions) ?? definition.goal : definition.goal;
    if (effectiveGoal?.type !== 'THRESHOLD') {
      const goalText = effectiveGoal.min === effectiveGoal.max
        ? formatAmount(effectiveGoal.min)
        : `${formatAmount(effectiveGoal.min)} – ${formatAmount(effectiveGoal.max)}`;
      return `${formatAmount(habitSummary.currentAmountCompletedOverGoalTimeInterval)} / ${goalText} ${habitSummary.goalUnit}`;
    }

    return `${formatAmount(habitSummary.currentAmountCompletedOverGoalTimeInterval)} / ${formatAmount((effectiveGoal && effectiveGoal.type === 'THRESHOLD') ? effectiveGoal.value : habitSummary.goalAmount)} ${habitSummary.goalUnit}`;
  };

  const calculateProgressInfo = (habitSummary: HabitSummary, definition: HabitDefinition | null) => {
    // Use unscaled goals from habit definition for modal progress
    // Use currentAmountCompletedOverGoalTimeInterval (today's progress) instead of overall report progress
    const current = habitSummary.currentAmountCompletedOverGoalTimeInterval;
    const effectiveGoal = definition?.childHabitIds?.length ? getEffectiveParentGoal(definition, childHabitDefinitions) ?? definition.goal : definition?.goal;

    if (!effectiveGoal || effectiveGoal.type === 'THRESHOLD') {
      // For THRESHOLD goals, or when the definition is unavailable, fall back to the scalar goal amount.
      const goalValue = effectiveGoal?.type === 'THRESHOLD' ? effectiveGoal.value : habitSummary.goalAmount;
      const percentage = goalValue > 0
        ? (current / goalValue) * 100
        : habitSummary.percentageCompletedOverGoalTimeInterval;
      return {
        percentage,
        displayText: `${formatNumber(percentage)}% Complete`,
        isNoData: false,
        isExceeded: false,
        progressBarValue: Math.min(percentage, 100),
        color: getProgressColor(percentage)
      };
    }

    const isAverageGoal = effectiveGoal.type === 'AVERAGE';

    if (isAverageGoal && habitSummary.hasEntriesOverGoalTimeInterval === false) {
      return {
        percentage: 0,
        displayText: 'No data',
        isNoData: true,
        isExceeded: false,
        progressBarValue: 0,
        color: 'error' as const
      };
    }

    // For range-like goals, use unscaled min/max from habit definition.
    const { min, max } = effectiveGoal;

    // Within range: 100% complete
    if (current >= min && current <= max) {
      return {
        percentage: 100,
        displayText: 'Within target range',
        isNoData: false,
        isExceeded: false,
        progressBarValue: 100,
        color: 'success' as const
      };
    }

    // Below min: show raw numbers
    if (current < min) {
      if (isAverageGoal) {
        return {
          percentage: 0,
          displayText: `Below minimum by ${formatNumber(min - current)} ${habitSummary.goalUnit}`,
          isNoData: false,
          isExceeded: false,
          progressBarValue: 0,
          color: 'error' as const
        };
      }
      const percentage = (current / min) * 100;
      return {
        percentage,
        displayText: `${formatAmount(current)} of ${formatAmount(min)} ${habitSummary.goalUnit} (minimum) completed`,
        isNoData: false,
        isExceeded: false,
        progressBarValue: Math.min(percentage, 100),
        color: 'error' as const
      };
    }

    // Above max: show raw numbers
    if (isAverageGoal) {
      return {
        percentage: 0,
        displayText: `Above maximum by ${formatNumber(current - max)} ${habitSummary.goalUnit}`,
        isNoData: false,
        isExceeded: true,
        progressBarValue: 0,
        color: 'success' as const // Will be overridden by black color
      };
    }
    const percentage = (current / max) * 100;
    return {
      percentage,
      displayText: `${formatAmount(current)} ${habitSummary.goalUnit} completed—ceiling is ${formatAmount(max)} ${habitSummary.goalUnit}`,
      isNoData: false,
      isExceeded: true,
      progressBarValue: Math.min(percentage, 100),
      color: 'success' as const // Will be overridden by black color
    };
  };

  const progressInfo = calculateProgressInfo(habit, habitDefinition);
  const sortedChildHabitDefinitions = [...childHabitDefinitions].sort((a, b) =>
    formatChildHabitDisplayName(a.habitDisplayName, habitName)
      .localeCompare(formatChildHabitDisplayName(b.habitDisplayName, habitName))
  );
  const sortedChildProgressItems = [...sortedChildHabitDefinitions]
    .map((childDefinition) => {
      const goalAmount = childDefinition.goal.type === 'THRESHOLD' ? childDefinition.goal.value : childDefinition.goal.max;
      const fallbackSummary: HabitSummary = {
        habitId: childDefinition.habitId,
        habitDisplayName: formatChildHabitDisplayName(childDefinition.habitDisplayName, habitName),
        goalAmountScaled: goalAmount,
        goalAmount: goalAmount,
        goalUnit: childDefinition.goalUnit,
        currentAmountCompletedOverReportTimeInterval: 0,
        currentAmountCompletedOverGoalTimeInterval: 0,
        percentageCompletedOverReportTimeInterval: 0,
        percentageCompletedOverGoalTimeInterval: 0,
        category: childDefinition.category,
        parentHabitId: childDefinition.parentHabitId,
        entries: [],
      };
      const summary = childHabitProgressById[childDefinition.habitId] || fallbackSummary;
      return {
        definition: childDefinition,
        summary,
        progress: calculateProgressInfo(summary, childDefinition),
      };
    });

  const getOffTrackChildHabitCount = (): number => {
    if (!habitDefinition?.childHabitIds?.length) return 0;

    const effectiveGoalForParent = getEffectiveParentGoal(habitDefinition, childHabitDefinitions) ?? habitDefinition.goal;
    const isParentGoalMet = effectiveGoalForParent.type === 'THRESHOLD'
      ? habit.currentAmountCompletedOverGoalTimeInterval >= effectiveGoalForParent.value
      : habit.currentAmountCompletedOverGoalTimeInterval >= effectiveGoalForParent.min &&
        habit.currentAmountCompletedOverGoalTimeInterval <= effectiveGoalForParent.max;

    if (!isParentGoalMet) return 0;

    return childHabitDefinitions.reduce((count, childDefinition) => {
      const childProgress = childHabitProgressById[childDefinition.habitId] || {
        habitId: childDefinition.habitId,
        habitDisplayName: childDefinition.habitDisplayName,
        goalAmountScaled: childDefinition.goal.type === 'THRESHOLD' ? childDefinition.goal.value : childDefinition.goal.max,
        goalAmount: childDefinition.goal.type === 'THRESHOLD' ? childDefinition.goal.value : childDefinition.goal.max,
        goalUnit: childDefinition.goalUnit,
        currentAmountCompletedOverReportTimeInterval: 0,
        currentAmountCompletedOverGoalTimeInterval: 0,
        percentageCompletedOverReportTimeInterval: 0,
        percentageCompletedOverGoalTimeInterval: 0,
        category: childDefinition.category,
        parentHabitId: childDefinition.parentHabitId,
        entries: [],
      };

      const childGoalMet = childDefinition.goal.type === 'THRESHOLD'
        ? childProgress.currentAmountCompletedOverGoalTimeInterval >= childDefinition.goal.value
        : childProgress.currentAmountCompletedOverGoalTimeInterval >= childDefinition.goal.min &&
          childProgress.currentAmountCompletedOverGoalTimeInterval <= childDefinition.goal.max;

      return childGoalMet ? count : count + 1;
    }, 0);
  };

  const offTrackChildHabitCount = getOffTrackChildHabitCount();
  const parentProgressBarColor = progressInfo.isNoData ? 'inherit' : offTrackChildHabitCount > 0 ? 'warning' : progressInfo.color;

  return (
    <>
    <GenericModal
      open={open && !activeChildHabit}
      onClose={() => { if (!manageCategoryConfirmOpen && !deleteConfirmOpen) onClose(); }}
      ariaLabelledBy="habit-detail-modal"
      ariaDescribedBy="habit-detail-description"
    >
      <Box
        sx={{
          ...(loading ? { height: '34vh', overflow: 'hidden' } : {}),
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        {loading ? (
          <Box>
            {/* Header skeleton */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} pr={8}>
              <Box flex={1} pr={2}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {habit?.habitDisplayName}
                </Typography>
              </Box>
              <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: 1 }} />
            </Box>

            {/* Description skeleton */}
            <Box mb={4}>
              <Skeleton variant="text" width="30%" height={32} sx={{ mb: 1 }} />
              <Box sx={{ p: 2, bgcolor: '#1A1A1A', borderRadius: 1 }}>
                <Skeleton variant="rectangular" width="40%" height={32} sx={{ mb: 2, borderRadius: '6px' }} />
                <Skeleton variant="text" width="95%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="88%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="92%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="70%" height={20} />
              </Box>
            </Box>

            {/* Progress skeleton */}
            <Box mb={4}>
              <Skeleton variant="text" width="35%" height={32} sx={{ mb: 1 }} />
              <Box sx={{ p: 3, bgcolor: '#1A1A1A', borderRadius: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} alignItems="center">
                  <Box flex={1} minWidth={0}>
                    <Skeleton variant="text" width="80%" height={36} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </Box>
                  <Box flex={2} minWidth={0}>
                    <Skeleton variant="rectangular" width="100%" height={12} sx={{ borderRadius: 6, mb: 1 }} />
                    <Skeleton variant="text" width="50%" height={28} sx={{ mx: 'auto' }} />
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        ) : (
          <>
        {/* Header with Version Dropdown */}
        <Box display="flex" alignItems="flex-start" gap={4} mb={3} pr={8}>
          <Box
            flex={1}
            onMouseEnter={handleNameMouseEnter}
            onMouseLeave={handleNameMouseLeave}
          >
            {isEditingName ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: nameSaveError ? 1 : 0 }}>
                  {isChildHabit && lockedChildNamePrefix && (
                    <Typography
                      variant="h4"
                      component="span"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        color: '#ffffff',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {lockedChildNamePrefix}
                    </Typography>
                  )}
                  <InputBase
                    autoFocus
                    value={nameText}
                    onChange={(e) => setNameText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') handleCancelEditName();
                    }}
                    disabled={isNameSaving}
                    sx={{
                      flex: 1,
                      fontFamily: '"Oswald", sans-serif',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      color: '#ffffff',
                      borderBottom: '2px solid #32CA1D',
                      '& input': { padding: 0 },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleSaveName}
                    disabled={isNameSaving}
                    sx={{ color: '#32CA1D', '&:hover': { bgcolor: 'rgba(50, 202, 29, 0.1)' } }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleCancelEditName}
                    disabled={isNameSaving}
                    sx={{ color: 'grey.400', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                {nameSaveError && (
                  <Alert severity="error" sx={{ mt: 0.5, py: 0, fontSize: '0.8rem' }}>
                    {nameSaveError}
                  </Alert>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {habitName}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Edit Habit Name"
                  onClick={handleStartEditName}
                  sx={{
                    color: '#32CA1D',
                    visibility: isHoveringName ? 'visible' : 'hidden',
                    '&:hover': {
                      bgcolor: 'rgba(50, 202, 29, 0.1)',
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
          
          {/* Version Dropdown */}
          {habitVersions.length > 0 && (
            <Box sx={{ minWidth: 180, maxWidth: 180 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedVersion}
                  onChange={handleVersionChange}
                  sx={{
                    bgcolor: '#1A1A1A',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#32CA1D',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#32CA1D',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#32CA1D',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        bgcolor: '#1A1A1A',
                        border: '1px solid #32CA1D',
                      },
                    },
                  }}
                >
                  {habitVersions.map((versionInfo) => (
                    <MenuItem 
                      key={versionInfo.version} 
                      value={versionInfo.version}
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <span>{getVersionLabel(versionInfo)}</span>
                        {hasInDraftChip(versionInfo) && (
                          <Chip 
                            label="IN DRAFT" 
                            size="small" 
                            sx={{ 
                              bgcolor: 'rgba(255, 152, 0, 0.3)',
                              color: '#FFB74D',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              height: 20,
                              border: '1px solid rgba(255, 167, 38, 0.5)',
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }} 
                          />
                        )}
                        {hasLatestPublishedChip(versionInfo) && (
                          <Chip 
                            label="LATEST" 
                            size="small" 
                            sx={{ 
                              bgcolor: '#32CA1D',
                              color: '#000',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              height: 20,
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }} 
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

            {/* Description */}
            {habitDefinition?.description && (
              <Box 
                mb={4}
              >
                <Box
                  data-testid="description-header-row"
                  sx={{ position: 'relative', mb: 1 }}
                  onMouseEnter={handleDescriptionMouseEnter}
                  onMouseLeave={handleDescriptionMouseLeave}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Description
                  </Typography>
                  <HoverActionIconButton
                    show={isVersionEditable() && !isEditingDescription && (showDescriptionActions || showNewDayAction)}
                    ariaLabel="Edit Description"
                    onClick={handleStartEdit}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#32CA1D',
                      '&:hover': {
                        bgcolor: 'rgba(50, 202, 29, 0.1)',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </HoverActionIconButton>
                </Box>

                {saveError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
                    {saveError}
                  </Alert>
                )}

                <Box 
                  ref={descriptionContainerRef}
                  sx={{ 
                    p: 2,
                    bgcolor: '#1A1A1A',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    ref={categoryChipRef}
                    data-testid="description-category-row"
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                    onMouseEnter={handleNewDayMouseEnter}
                    onMouseLeave={handleNewDayMouseLeave}
                  >
                    <Chip
                      label={isEditingDescription ? selectedCategory : (habitDefinition?.category || habit.category)}
                      variant="outlined"
                      size="medium"
                      color={getCategoryColor(isEditingDescription ? selectedCategory : (habitDefinition?.category || habit.category))}
                      onClick={handleCategoryClick}
                      sx={{ 
                        fontSize: '1rem', 
                        px: 2, 
                        py: 1,
                        fontWeight: 500,
                        borderRadius: '6px',
                        cursor: isEditingDescription ? 'pointer' : 'default',
                        pointerEvents: isEditingDescription ? 'auto' : 'none',
                        '&:hover': isEditingDescription ? {
                          borderColor: '#32CA1D',
                          bgcolor: 'rgba(50, 202, 29, 0.1)',
                        } : {},
                      }}
                    />
                    <HoverActionIconButton
                      show={isDoDayTasksHabit && isVersionEditable() && !isEditingDescription && showNewDayAction}
                      ariaLabel="New Day"
                      onClick={handleCreateNewDay}
                      tooltipTitle="New Day"
                      tooltipArrow={true}
                      sx={{
                        color: '#32CA1D',
                        ml: 1,
                        '&:hover': {
                          bgcolor: 'rgba(50, 202, 29, 0.1)',
                        },
                      }}
                    >
                      <NoteAddIcon fontSize="small" />
                    </HoverActionIconButton>
                  </Box>

                  {/* Category Dropdown Menu */}
                  <Popover
                    open={Boolean(categoryAnchorEl)}
                    anchorEl={categoryAnchorEl}
                    onClose={handleCategoryClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      sx: {
                        bgcolor: '#1A1A1A',
                        border: '1px solid #32CA1D',
                        borderRadius: 1,
                        mt: 1,
                        maxHeight: 300,
                        overflow: 'auto',
                      },
                    }}
                  >
                    <Box sx={{ p: 1 }}>
                      {categories.map((category) => (
                        <Box
                          key={category}
                          onClick={(e) => handleCategorySelect(category, e)}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            p: 0.5,
                            cursor: 'pointer',
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'rgba(50, 202, 29, 0.1)',
                            },
                          }}
                        >
                          <Chip
                            label={category}
                            variant="outlined"
                            size="medium"
                            color={getCategoryColor(category)}
                            sx={{ 
                              fontSize: '0.9rem', 
                              px: 2, 
                              py: 1,
                              fontWeight: 500,
                              borderRadius: '6px',
                              width: '100%',
                              cursor: 'pointer',
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Popover>

                  {isEditingDescription ? (
                    <Box ref={editFormRef} onKeyDown={handleKeyPress}>
                      <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={descriptionText}
                        onChange={(e) => setDescriptionText(e.target.value)}
                        onBlur={handleDescriptionFieldBlur}
                        placeholder="Enter description (supports Markdown)"
                        disabled={isSaving}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#000',
                            fontFamily: 'monospace',
                            '& fieldset': {
                              borderColor: '#32CA1D',
                            },
                            '&:hover fieldset': {
                              borderColor: '#32CA1D',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#32CA1D',
                            },
                          },
                        }}
                      />
                      {!hideEditActionButtons && (
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                            startIcon={<CloseIcon />}
                            sx={{
                              borderColor: 'grey.600',
                              color: 'grey.300',
                              '&:hover': {
                                borderColor: 'grey.500',
                                bgcolor: 'rgba(255, 255, 255, 0.05)',
                              },
                            }}
                          >
                            Cancel
                          </Button>
                          <SaveActionButton
                            onClick={handleSaveDescription}
                            disabled={isSaving || (descriptionText === habitDefinition?.description && selectedCategory === (habitDefinition?.category || habit?.category))}
                            isSaving={isSaving}
                            text="SAVE"
                            ariaLabel="Save"
                            size="small"
                          />
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box 
                      data-testid="habit-description"
                      sx={{ 
                        cursor: 'default',
                        '& p': { 
                          lineHeight: 1.6,
                          mb: 1,
                          fontSize: '1rem',
                        },
                        '& ul': {
                          pl: 3,
                          mb: 1,
                          listStyleType: 'disc',
                          listStylePosition: 'inside',
                        },
                        '& ol': {
                          pl: 3,
                          mb: 1,
                          listStyleType: 'decimal',
                          listStylePosition: 'inside',
                        },
                        '& li': {
                          mb: 0.5,
                        },
                        '& a': {
                          color: 'primary.main',
                          textDecoration: 'underline',
                        },
                        '& table': {
                          width: '100%',
                          borderCollapse: 'collapse',
                          mb: 2,
                        },
                        '& th, & td': {
                          border: '1px solid',
                          borderColor: 'divider',
                          p: 1,
                          textAlign: 'left',
                        },
                        '& th': {
                          bgcolor: AIRMASK_GREEN,
                          color: AIRMASK_DARK,
                          fontWeight: 600,
                        },
                        '& code': {
                          bgcolor: AIRMASK_SURFACE_GRAY,
                          color: 'text.primary',
                          px: 0.5,
                          borderRadius: 0.5,
                          fontSize: '0.875em',
                        },
                        '& pre': {
                          bgcolor: AIRMASK_SURFACE_GRAY,
                          color: 'text.primary',
                          p: 1,
                          borderRadius: 1,
                          overflow: 'auto',
                        },
                      }}
                    >
                      {(() => {
                        // Find a checkbox input in the current item's content (recurse into <p> wrappers, but not into nested <ul>/<ol>/<li>)
                        const findCheckbox = (children: React.ReactNode): any => {
                          for (const child of React.Children.toArray(children)) {
                            const c = child as any;
                            if (c?.type === 'input' && c?.props?.type === 'checkbox') return c;
                            // Don't recurse into nested lists — those are separate items
                            if (c?.type === 'ul' || c?.type === 'ol' || c?.type === 'li') continue;
                            if (c?.props?.children) {
                              const found = findCheckbox(c.props.children);
                              if (found) return found;
                            }
                          }
                          return null;
                        };

                        // Remove the native checkbox input from the current item's content only
                        // (don't recurse into nested <ul>/<ol>/<li> — those have their own li override)
                        // Also convert <p> to <span> to prevent block-level line breaks after the MUI checkbox
                        const removeCheckbox = (children: React.ReactNode): React.ReactNode => {
                          return React.Children.map(children, (child) => {
                            const c = child as any;
                            if (c?.type === 'input' && c?.props?.type === 'checkbox') return null;
                            // Don't recurse into nested lists — their checkboxes are handled by their own li override
                            if (c?.type === 'ul' || c?.type === 'ol' || c?.type === 'li') return child;
                            if (c?.props?.children) {
                              const newChildren = removeCheckbox(c.props.children);
                              // Convert <p> to <span> so content stays inline with the checkbox
                              if (c.type === 'p') {
                                const { children: _, ...restProps } = c.props;
                                return <span {...restProps}>{newChildren}</span>;
                              }
                              return React.cloneElement(c, {}, newChildren);
                            }
                            return child;
                          });
                        };

                        return (
                          <MarkdownRenderer
                            components={{
                              ...markdownLinkComponent,
                              li: ({ node, children, ...props }: any) => {
                                const checkboxChild = findCheckbox(children);

                                if (checkboxChild) {
                                  const isChecked = checkboxChild?.props?.checked || false;
                                  const lineNumber = typeof node?.position?.start?.line === 'number'
                                    ? node.position.start.line - 1
                                    : null;

                                  return (
                                    <li {...props} style={{ listStyle: 'none' }}>
                                      <Checkbox
                                        checked={isChecked}
                                        onChange={() => {
                                          if (lineNumber !== null) {
                                            handleCheckboxToggle(lineNumber);
                                          }
                                        }}
                                        disabled={!isVersionEditable()}
                                        size="small"
                                        icon={<CheckBoxOutlineBlankIcon style={{ color: '#32CA1D' }} fontSize="small" />}
                                        checkedIcon={<CheckBoxIcon style={{ color: '#32CA1D' }} fontSize="small" />}
                                        sx={{
                                          p: 0,
                                          mr: 1,
                                          '&:hover': { bgcolor: 'rgba(50, 202, 29, 0.08)' },
                                        }}
                                      />
                                      {removeCheckbox(children)}
                                    </li>
                                  );
                                }
                                return <li {...props}>{children}</li>;
                              },
                            }}
                          >
                            {habitDefinition.description}
                          </MarkdownRenderer>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Blocks section is hidden for Manage category by default, but the block-graph subsection
                should still appear when the graph is loading, errored, or has data available. */}
            {!isChildHabit && ((habitDefinition?.category ?? habit?.category) !== 'Manage' || graphLoading || graphError || graphData !== null) && (
              <Box
                ref={blocksContainerRef}
                mb={4}
                onMouseEnter={handleBlocksMouseEnter}
                onMouseLeave={handleBlocksMouseLeave}
              >
                <Box sx={{ position: 'relative', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Blocks</Typography>
                  <HoverActionIconButton
                    show={isHoveringBlocks || isEditingBlocks}
                    ariaLabel="Edit Blocks"
                    onClick={() => setIsEditingBlocks((prev) => !prev)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#32CA1D',
                      bgcolor: isEditingBlocks ? 'rgba(50, 202, 29, 0.15)' : 'transparent',
                      '&:hover': {
                        bgcolor: 'rgba(50, 202, 29, 0.1)',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </HoverActionIconButton>
                </Box>
                {blocksLoading && (
                  <Box sx={{ p: 2, bgcolor: '#1A1A1A', borderRadius: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={i} variant="rectangular" width={144} height={144} sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.08)' }} />
                    ))}
                  </Box>
                )}
                {blocksError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBlocksError(null)}>
                    {blocksError}
                  </Alert>
                )}
                {!blocksLoading && blockSequences.length === 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    No block sequences for this category.
                  </Typography>
                )}
                {!blocksLoading && blockSequences.length > 0 && (
                  <Box sx={{ p: 2, bgcolor: '#1A1A1A', borderRadius: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    {blockSequences.map((seq) => (
                      <BlockSequenceDisplay
                        key={seq.blockSequenceId}
                        orientation="vertical"
                        sequence={seq}
                        arrowColor="#666666"
                        editMode={isEditingBlocks}
                        onBlockTextChange={async (blockSequenceId, blockId, newText) => {
                          const result = await blockService.updateBlockText(blockSequenceId, blockId, newText);
                          if (!result.success) {
                            setBlocksError(result.error ?? 'Failed to update block text');
                            return;
                          }
                          // Optimistically update local state so the UI reflects the change
                          setBlockSequences((prev) =>
                            prev.map((s) =>
                              s.blockSequenceId !== blockSequenceId
                                ? s
                                : {
                                    ...s,
                                    blocks: s.blocks.map((b) =>
                                      b.blockId !== blockId ? b : { ...b, text: newText }
                                    ),
                                  }
                            )
                          );
                        }}
                        onBlockCreate={async (blockSequenceId, predecessorBlockId) => {
                          const result = await blockService.createBlock(blockSequenceId, predecessorBlockId);
                          if (!result.success || !result.data) {
                            return { success: false, error: result.error ?? 'Failed to add block' };
                          }
                          const newBlock = result.data;
                          // Optimistically splice the new block into the correct linked-list position
                          setBlockSequences((prev) =>
                            prev.map((s) => {
                              if (s.blockSequenceId !== blockSequenceId) return s;
                              const newBlocks = [...s.blocks];
                              if (predecessorBlockId === '') {
                                // Insert at head
                                newBlocks.unshift(newBlock);
                                return { ...s, headBlock: newBlock.blockId, blocks: newBlocks };
                              } else {
                                // Insert after predecessor
                                const predIdx = newBlocks.findIndex((b) => b.blockId === predecessorBlockId);
                                if (predIdx === -1) {
                                  newBlocks.push(newBlock); // fallback: append
                                } else {
                                  newBlocks.splice(predIdx + 1, 0, newBlock);
                                }
                                return { ...s, blocks: newBlocks };
                              }
                            })
                          );
                          return { success: true };
                        }}
                        onBlockDelete={async (blockSequenceId, blockId) => {
                          const result = await blockService.deleteBlock(blockSequenceId, blockId);
                          if (!result.success) {
                            return { success: false, error: result.error ?? 'Failed to delete block' };
                          }
                          setBlockSequences((prev) =>
                            prev.map((s) => {
                              if (s.blockSequenceId !== blockSequenceId) return s;
                              const newBlocks = s.blocks.filter((b) => b.blockId !== blockId);
                              const newHead = s.headBlock === blockId
                                ? (newBlocks.length > 0 ? newBlocks[0].blockId : 'None')
                                : s.headBlock;
                              return { ...s, headBlock: newHead, blocks: newBlocks };
                            })
                          );
                          return { success: true };
                        }}
                        onBlockComplete={async (blockSequenceId, blockId) => {
                          // Optimistically move the block from active to completed.
                          let completedBlock: import('../types/api').BlockItem | undefined;
                          setBlockSequences((prev) =>
                            prev.map((s) => {
                              if (s.blockSequenceId !== blockSequenceId) return s;
                              const blockIdx = s.blocks.findIndex((b) => b.blockId === blockId);
                              if (blockIdx === -1) return s;
                              completedBlock = { ...s.blocks[blockIdx], nextBlock: 'None' };
                              const newBlocks = s.blocks.filter((b) => b.blockId !== blockId);
                              const newHead = s.headBlock === blockId
                                ? (newBlocks.length > 0 ? newBlocks[0].blockId : 'None')
                                : s.headBlock;
                              const newCompleted = [...(s.completedBlocks ?? []), completedBlock!];
                              const newHeadCompleted = s.completedBlocks?.length === 0 || s.headCompletedBlock === 'None'
                                ? blockId
                                : s.headCompletedBlock;
                              return { ...s, headBlock: newHead, blocks: newBlocks, headCompletedBlock: newHeadCompleted, completedBlocks: newCompleted };
                            })
                          );

                          const result = await blockService.completeBlock(blockSequenceId, blockId);
                          if (!result.success) {
                            // Revert the optimistic update.
                            setBlockSequences((prev) =>
                              prev.map((s) => {
                                if (s.blockSequenceId !== blockSequenceId || !completedBlock) return s;
                                const revertedBlocks = [...s.blocks, completedBlock].sort(
                                  (a, b) => a.createdAt.localeCompare(b.createdAt)
                                );
                                const revertedCompleted = s.completedBlocks.filter((b) => b.blockId !== blockId);
                                return { ...s, blocks: revertedBlocks, completedBlocks: revertedCompleted };
                              })
                            );
                            return { success: false, error: result.error ?? 'Failed to complete block' };
                          }
                          return { success: true };
                        }}
                      />
                    ))}
                  </Box>
                )}

                {(graphLoading || graphError || graphData !== null) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Block Nodes</Typography>
                    {graphLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} sx={{ color: '#32CA1D' }} />
                        <Typography variant="body2" color="text.secondary">Loading block graph…</Typography>
                      </Box>
                    ) : graphError ? (
                      <Alert severity="error">{graphError}</Alert>
                    ) : (
                      <BlockNodeGraph graph={graphData ?? { success: true, nodes: [], edges: [] }} habitName={activeHabitName} title="Block Nodes" />
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Completed Blocks Section — shown when any sequence has completed blocks */}
            {!isChildHabit && (habitDefinition?.category ?? habit?.category) !== 'Manage' && blockSequences.some((s) => s.completedBlocks?.length > 0) && (
              <Box
                ref={completedBlocksContainerRef}
                mb={4}
                onMouseEnter={handleCompletedBlocksMouseEnter}
                onMouseLeave={handleCompletedBlocksMouseLeave}
              >
                <Box sx={{ position: 'relative', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Completed Blocks</Typography>
                  <HoverActionIconButton
                    show={isHoveringCompletedBlocks || isEditingCompletedBlocks}
                    ariaLabel="Edit Completed Blocks"
                    onClick={() => setIsEditingCompletedBlocks((prev) => !prev)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#32CA1D',
                      bgcolor: isEditingCompletedBlocks ? 'rgba(50, 202, 29, 0.15)' : 'transparent',
                      '&:hover': {
                        bgcolor: 'rgba(50, 202, 29, 0.1)',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </HoverActionIconButton>
                </Box>
                <Box sx={{ p: 2, bgcolor: '#1A1A1A', borderRadius: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  {blockSequences
                    .filter((seq) => seq.completedBlocks?.length > 0)
                    .map((seq) => (
                      <BlockSequenceDisplay
                        key={seq.blockSequenceId}
                        orientation="vertical"
                        sequence={{ ...seq, blocks: seq.completedBlocks, headBlock: seq.headCompletedBlock }}
                        arrowColor="#666666"
                        editMode={isEditingCompletedBlocks}
                        onBlockTextChange={async (blockSequenceId, blockId, newText) => {
                          const result = await blockService.updateBlockText(blockSequenceId, blockId, newText);
                          if (!result.success) {
                            setBlocksError(result.error ?? 'Failed to update block text');
                            return;
                          }
                          setBlockSequences((prev) =>
                            prev.map((s) =>
                              s.blockSequenceId !== blockSequenceId
                                ? s
                                : {
                                    ...s,
                                    completedBlocks: s.completedBlocks.map((b) =>
                                      b.blockId !== blockId ? b : { ...b, text: newText }
                                    ),
                                  }
                            )
                          );
                        }}
                      />
                    ))}
                </Box>
              </Box>
            )}

            {/* Progress Section */}
            {!isChildHabit && childHabitDefinitions.length > 0 && (
              <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Child Habits
                </Typography>
                <Box component="ul" sx={{ p: 2, bgcolor: '#1A1A1A', borderRadius: 1, m: 0, pl: 3, textAlign: 'left', listStyle: 'disc', listStylePosition: 'outside' }}>
                  {sortedChildHabitDefinitions.map((childHabit) => (
                    <Box component="li" key={childHabit.habitId} sx={{ color: '#fff', mb: 0.5, textAlign: 'left', width: '100%' }}>
                      <Button
                        onClick={() => void handleOpenChildHabit(childHabit)}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          width: '100%',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          color: AIRMASK_GREEN,
                          textTransform: 'none',
                          fontWeight: 500,
                          textDecoration: 'underline',
                          '&:hover': { bgcolor: 'transparent', color: AIRMASK_GREEN },
                        }}
                      >
                        {formatChildHabitDisplayName(childHabit.habitDisplayName, habitName)}
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Box mb={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Progress Overview
                {habitDefinition?.intervalUnit && (() => {
                  const unit = habitDefinition.intervalUnit.toLowerCase();
                  let periodText = '';
                  if (unit === 'day') periodText = ' (Today)';
                  else if (unit === 'week') periodText = ' (This Week)';
                  else if (unit === 'month') periodText = ' (This Month)';
                  else if (unit === 'year') periodText = ' (This Year)';
                  
                  return periodText ? (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {periodText}
                    </Box>
                  ) : null;
                })()}
              </Typography>
              <Box sx={{ p: 3, bgcolor: '#1A1A1A', borderRadius: 1 }}>
                {sortedChildProgressItems.length > 0 ? (
                  <Stack spacing={2.5}>
                    {sortedChildProgressItems.map((item) => (
                      <Box key={item.definition.habitId}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.75 }}
                          data-testid={`progress-overview-child-label-${item.definition.habitId}`}
                        >
                          {formatChildHabitDisplayName(item.definition.habitDisplayName, habitName)}
                        </Typography>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                          {formatProgressAmount(item.summary, item.definition)}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={item.progress.progressBarValue}
                          color={item.progress.color}
                          sx={{ 
                            height: 10,
                            borderRadius: 5,
                            mb: 0.75,
                            ...(item.progress.isNoData && {
                              backgroundColor: 'rgba(255, 255, 255, 0.18)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: 'rgba(255, 255, 255, 0.45)'
                              }
                            }),
                            ...(item.progress.isExceeded && {
                              backgroundColor: 'rgba(255, 255, 255, 0.12)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#000000'
                              }
                            })
                          }}
                        />
                        <Tooltip
                          title={item.progress.isExceeded ? "% exceeded by is calculated by taking the average of the target goal range" : ""}
                          arrow
                          disableHoverListener={!item.progress.isExceeded}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.progress.displayText}
                          </Typography>
                        </Tooltip>
                      </Box>
                    ))}

                    <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.12)', pt: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.75 }}
                        data-testid="progress-overview-parent-label"
                      >
                        {habit.habitDisplayName}
                      </Typography>
                      <Typography variant="h5" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                        {formatProgressAmount(habit, habitDefinition)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={progressInfo.progressBarValue}
                        color={parentProgressBarColor}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          mb: 1,
                          ...(progressInfo.isNoData && {
                            backgroundColor: 'rgba(255, 255, 255, 0.18)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: 'rgba(255, 255, 255, 0.45)'
                            }
                          }),
                          ...(progressInfo.isExceeded && {
                            backgroundColor: 'rgba(255, 255, 255, 0.12)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#000000'
                            }
                          })
                        }}
                      />
                      <Tooltip
                        title={progressInfo.isExceeded ? "% exceeded by is calculated by taking the average of the target goal range" : ""}
                        arrow
                        disableHoverListener={!progressInfo.isExceeded}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                            {progressInfo.displayText}
                          </Typography>
                          {offTrackChildHabitCount > 0 && (
                            <>
                              <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                                {`(Child Habit${offTrackChildHabitCount === 1 ? '' : 's'} Off-Track)`}
                              </Typography>
                              <WarningAmberRoundedIcon sx={{ color: 'warning.main', fontSize: 22 }} />
                            </>
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                  </Stack>
                ) : (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} alignItems="center">
                    <Box flex={1} minWidth={0}>
                      <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                        {formatProgressAmount(habit, habitDefinition)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Current Progress
                      </Typography>
                    </Box>
                    <Box flex={2} minWidth={0}>
                      <LinearProgress
                        variant="determinate"
                        value={progressInfo.progressBarValue}
                        color={progressInfo.color}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          mb: 1,
                          ...(progressInfo.isNoData && {
                            backgroundColor: 'rgba(255, 255, 255, 0.18)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: 'rgba(255, 255, 255, 0.45)'
                            }
                          }),
                          ...(progressInfo.isExceeded && {
                            backgroundColor: 'rgba(255, 255, 255, 0.12)', // Gray background for unfilled portion
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#000000'
                            }
                          })
                        }}
                      />
                      <Tooltip
                        title={progressInfo.isExceeded ? "% exceeded by is calculated by taking the average of the target goal range" : ""}
                        arrow
                        disableHoverListener={!progressInfo.isExceeded}
                      >
                        <Typography variant="h6" align="center" sx={{ fontWeight: 600 }}>
                          {progressInfo.displayText}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Recent Entries */}
            {habit.entries && habit.entries.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Recent Entries
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {habit.entries.map((entry, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        p: 2, 
                        mb: 1, 
                        bgcolor: 'background.default', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {entry.count} {habit.goalUnit}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(entry.timestamp).toLocaleDateString()}
                        </Typography>
                      </Stack>
                      {entry.comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          "{entry.comment}"
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Goal Information */}
            <Box 
              mt={4}
              onMouseEnter={handleGoalMouseEnter}
              onMouseLeave={handleGoalMouseLeave}
              sx={{
                transition: 'all 0.7s ease-in-out',
              }}
            >
              <Box sx={{ position: 'relative', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Goal
                </Typography>
                {canEditGoal && isHoveringGoal && !isEditingGoal && (
                  <IconButton
                    size="small"
                    aria-label="Edit Goal"
                    onClick={handleStartEditGoal}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#32CA1D',
                      '&:hover': {
                        bgcolor: 'rgba(50, 202, 29, 0.1)',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {isEditingGoal ? (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(50, 202, 29, 0.1)', 
                  border: '1px solid rgba(50, 202, 29, 0.3)',
                  borderRadius: 1,
                  transition: 'all 0.7s ease-in-out',
                }}>
                  <ParentChildGoalEditor
                    childHabits={childHabitDefinitions.map((child) => ({
                      habitId: child.habitId,
                      habitDisplayName: formatChildHabitDisplayName(child.habitDisplayName, habitName),
                    }))}
                    childGoalAmounts={childGoalAmounts}
                    onChildGoalAmountChange={(habitId, patch) => {
                      setChildGoalAmounts((prev) => ({
                        ...prev,
                        [habitId]: { ...prev[habitId], ...patch },
                      }));
                    }}
                    parentHabitDisplayName={habit?.habitDisplayName}
                    selectedGoalType={selectedGoalType}
                    onSelectedGoalTypeChange={(goalType) => setSelectedGoalType(goalType)}
                    goalUnit={goalUnit}
                    onGoalUnitChange={(value) => setGoalUnit(value)}
                    intervalUnit={intervalUnit}
                    onIntervalUnitChange={(value) => setIntervalUnit(value)}
                    thresholdValue={thresholdValue}
                    onThresholdValueChange={(value) => {
                      setThresholdValue(value);
                      setGoalError('');
                    }}
                    rangeMin={rangeMin}
                    onRangeMinChange={(value) => {
                      setRangeMin(value);
                      setGoalError('');
                    }}
                    rangeMax={rangeMax}
                    onRangeMaxChange={(value) => {
                      setRangeMax(value);
                      setGoalError('');
                    }}
                    availableGoalUnits={availableGoalUnits}
                    availableIntervalUnits={availableIntervalUnits}
                    parentAmountsAsText={isParentGoalAutoCalculated}
                  />

                  {/* Error Message */}
                  {goalError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {goalError}
                    </Alert>
                  )}

                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ 
                    mt: 2,
                    opacity: 0,
                    animation: 'fadeInSync 0.7s ease-in-out forwards',
                    '@keyframes fadeInSync': {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                    },
                  }}>
                    <Button
                      size="small"
                      onClick={handleCancelEditGoal}
                      sx={{ color: '#ffffff' }}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveGoal}
                      disabled={isSaving}
                      sx={{
                        bgcolor: '#32CA1D',
                        '&:hover': {
                          bgcolor: '#28a518',
                        },
                      }}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Stack>
                </Box>
              ) : (() => {
                const goalForDisplay = effectiveParentGoal ?? habitDefinition?.goal ?? { type: 'THRESHOLD' as const, value: habit.goalAmount };

                return (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgba(50, 202, 29, 0.1)', 
                    border: '1px solid rgba(50, 202, 29, 0.3)',
                    borderRadius: 1,
                    transition: 'all 0.7s ease-in-out',
                  }}>
                    {goalForDisplay.type === 'THRESHOLD' ? (
                    <Typography variant="body2" sx={{ 
                      color: '#32CA1D',
                      transition: 'font-size 0.7s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}>
                      <strong>Goal:</strong> <span style={{ color: '#ffffff' }}>{goalForDisplay.value} {habitDefinition?.goalUnit || habit.goalUnit} per{' '}
                      {habitDefinition?.intervalUnit?.toLowerCase() || 'week'}</span>
                      <Tooltip title={getGoalTooltipText('THRESHOLD', goalForDisplay, habitDefinition?.goalUnit || habit.goalUnit, habitDefinition?.intervalUnit || 'week')} arrow placement="top">
                        <InfoIcon sx={{ fontSize: '1rem', color: 'rgba(50, 202, 29, 0.83)', cursor: 'help' }} />
                      </Tooltip>
                    </Typography>
                  ) : goalForDisplay.type === 'RANGE' || goalForDisplay.type === 'AVERAGE' ? (
                    <Typography variant="body2" sx={{ 
                      color: '#32CA1D',
                      transition: 'font-size 0.7s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}>
                      <strong>{goalForDisplay.type === 'AVERAGE' ? 'Average Goal:' : 'Goal Range:'}</strong> <span style={{ color: '#ffffff' }}>
                        {goalForDisplay.min === goalForDisplay.max 
                          ? formatAmount(goalForDisplay.min)
                          : `${formatAmount(goalForDisplay.min)} – ${formatAmount(goalForDisplay.max)}`} {habitDefinition?.goalUnit || habit.goalUnit} per{' '}
                      {habitDefinition?.intervalUnit?.toLowerCase() || 'week'}</span>
                      <Tooltip title={getGoalTooltipText(goalForDisplay.type, goalForDisplay, habitDefinition?.goalUnit || habit.goalUnit, habitDefinition?.intervalUnit || 'week')} arrow placement="top">
                        <InfoIcon sx={{ fontSize: '1rem', color: 'rgba(50, 202, 29, 0.83)', cursor: 'help' }} />
                      </Tooltip>
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ 
                      color: '#32CA1D',
                      transition: 'font-size 0.7s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}>
                      <strong>Goal:</strong> <span style={{ color: '#ffffff' }}>{habit.goalAmount} {habitDefinition?.goalUnit || habit.goalUnit} per{' '}
                      {habitDefinition?.intervalUnit?.toLowerCase() || 'week'}</span>
                      <Tooltip title="Threshold: A minimum target to reach or exceed" arrow placement="top">
                        <InfoIcon sx={{ fontSize: '1rem', color: 'rgba(50, 202, 29, 0.83)', cursor: 'help' }} />
                      </Tooltip>
                    </Typography>
                  )}
                  </Box>
                );
              })()}
            </Box>
          </>
        )}
      </Box>
    </GenericModal>

    {/* Right-click context menu */}
    <ContextMenu
      position={open && !activeChildHabit ? contextMenuPosition : null}
      onClose={handleCloseContextMenu}
      paperSx={{
        bgcolor: '#1A1A1A',
        border: '1px solid #333',
        minWidth: 180,
      }}
      sx={{ zIndex: 1400 }}
    >
      {habitVersions.find(v => v.version === selectedVersion) &&
        !habitVersions.find(v => v.version === selectedVersion)!.isLatestPublishedVersion && (
        <MenuItem
          onClick={handlePublishVersionClick}
          disabled={isPublishingVersion}
          sx={{ gap: 1.5 }}
        >
          <CheckIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography sx={{ fontSize: '0.9rem' }}>Publish Habit Version</Typography>
        </MenuItem>
      )}
      <MenuItem
        onClick={handleCreateNewVersionClick}
        disabled={isCreatingVersion}
        sx={{ gap: 1.5 }}
      >
        <AddIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography sx={{ fontSize: '0.9rem' }}>Create New Version</Typography>
      </MenuItem>
      {!isChildHabit && (
        <MenuItem
          onClick={handleCreateChildHabitClick}
          sx={{ gap: 1.5 }}
        >
          <LanIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography sx={{ fontSize: '0.9rem' }}>Create Child Habit</Typography>
        </MenuItem>
      )}
      <MenuItem
        onClick={handleDeleteClick}
        sx={{
          gap: 1.5,
          '& .delete-icon': { color: 'text.secondary' },
          '& .delete-label': { color: 'text.primary', fontSize: '0.9rem' },
          '&:hover .delete-icon': { color: '#f44336' },
          '&:hover .delete-label': { color: '#f44336' },
        }}
      >
        <DeleteIcon fontSize="small" className="delete-icon" />
        <Typography className="delete-label">Delete Habit</Typography>
      </MenuItem>
    </ContextMenu>

    {/* Delete confirmation dialog */}
    <Dialog
      open={deleteConfirmOpen}
      onClose={handleDeleteCancel}
      sx={{ zIndex: 1500 }}
      PaperProps={{
        sx: {
          bgcolor: '#1A1A1A',
          border: '1px solid #333',
          borderRadius: 2,
          minWidth: 360,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <DeleteIcon sx={{ color: '#f44336' }} />
        {deleteStep === 1 ? 'Are you sure?' : 'Confirm deletion'}
      </DialogTitle>
      <DialogContent>
        {deleteStep === 1 ? (
          <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            If you delete this habit, the habit description and all associated data will be lost.
          </Typography>
        ) : (
          <>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 1 }}>
              Type <Box component="span" sx={{ color: '#fff', fontWeight: 600 }}>{habit?.habitDisplayName}</Box> to confirm:
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && deleteConfirmText === habit?.habitDisplayName && !isDeleting) {
                  handleDeleteConfirm();
                }
              }}
              placeholder={habit?.habitDisplayName}
              disabled={isDeleting}
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#000',
                  '& fieldset': { borderColor: deleteConfirmText === habit?.habitDisplayName ? '#f44336' : '#555' },
                  '&:hover fieldset': { borderColor: deleteConfirmText === habit?.habitDisplayName ? '#f44336' : '#777' },
                  '&.Mui-focused fieldset': { borderColor: deleteConfirmText === habit?.habitDisplayName ? '#f44336' : '#32CA1D' },
                },
              }}
            />
            {deleteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteError}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {deleteStep === 1 ? (
          <>
            <Button
              variant="outlined"
              onClick={handleDeleteCancel}
              sx={{
                borderColor: 'grey.600',
                color: 'grey.300',
                '&:hover': { borderColor: 'grey.400', bgcolor: 'rgba(255,255,255,0.05)' },
              }}
            >
              No
            </Button>
            <Button
              variant="contained"
              onClick={() => setDeleteStep(2)}
              sx={{
                bgcolor: '#f44336',
                color: '#fff',
                '&:hover': { bgcolor: '#d32f2f' },
              }}
            >
              Yes
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={handleDeleteCancel}
              disabled={isDeleting}
              sx={{
                borderColor: 'grey.600',
                color: 'grey.300',
                '&:hover': { borderColor: 'grey.400', bgcolor: 'rgba(255,255,255,0.05)' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={isDeleting || deleteConfirmText !== habit?.habitDisplayName}
              startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
              sx={{
                bgcolor: '#f44336',
                color: '#fff',
                '&:hover': { bgcolor: '#d32f2f' },
                '&.Mui-disabled': { bgcolor: 'grey.700', color: 'grey.500' },
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

    {!isChildHabit && (
      <CreateHabitModal
        open={isChildHabitCreateOpen && !activeChildHabit}
        onClose={() => setIsChildHabitCreateOpen(false)}
        onHabitCreated={() => {
          onHabitUpdated?.();
        }}
        parentHabit={habit && habitDefinition ? {
          habitId: habit.habitId,
          habitDisplayName: habit.habitDisplayName,
          category: habit.category,
          goal: habitDefinition.goal,
          goalUnit: habitDefinition.goalUnit,
          intervalUnit: habitDefinition.intervalUnit,
          versionSK: selectedVersion,
          lastModifiedAt: habitDefinition.lastModifiedAt || '',
          isPublished: habitVersions.find(v => v.version === selectedVersion)?.isLatestPublishedVersion ?? false,
        } : null}
      />
    )}

    {activeChildHabit && (
      <HabitDetailModal
        open={true}
        habit={activeChildHabit.habit}
        habitVersions={activeChildHabit.habitVersions}
        isChildHabit={true}
        currentParentHabitDisplayName={activeChildHabit.parentHabitDisplayName}
        onClose={() => {
          setActiveChildHabit(null);
        }}
        onHabitUpdated={() => {
          onHabitUpdated?.();
        }}
        onHabitDeleted={onHabitDeleted}
      />
    )}

    {/* Change-to-Manage category confirmation dialog */}
    <Dialog
      open={manageCategoryConfirmOpen}
      onClose={handleManageCategoryCancel}
      sx={{ zIndex: 1500 }}
      PaperProps={{
        sx: {
          bgcolor: '#1A1A1A',
          border: '1px solid #333',
          borderRadius: 2,
          minWidth: 360,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <DeleteIcon sx={{ color: '#f44336' }} />
        {manageCategoryConfirmStep === 1 ? 'Delete blocks?' : 'Confirm category change'}
      </DialogTitle>
      <DialogContent>
        {manageCategoryConfirmStep === 1 ? (
          <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Changing the category to <Box component="span" sx={{ color: '#fff', fontWeight: 600 }}>Manage</Box> will
            permanently delete all blocks for this habit. This cannot be undone.
          </Typography>
        ) : (
          <>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 1 }}>
              Type <Box component="span" sx={{ color: '#fff', fontWeight: 600 }}>{habit?.habitDisplayName}</Box> to confirm:
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={manageCategoryConfirmText}
              onChange={(e) => setManageCategoryConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && manageCategoryConfirmText === habit?.habitDisplayName && !isManageCategorySaving) {
                  handleManageCategoryConfirm();
                }
              }}
              placeholder={habit?.habitDisplayName}
              disabled={isManageCategorySaving}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#000',
                  '& fieldset': { borderColor: manageCategoryConfirmText === habit?.habitDisplayName ? '#f44336' : '#555' },
                  '&:hover fieldset': { borderColor: manageCategoryConfirmText === habit?.habitDisplayName ? '#f44336' : '#777' },
                  '&.Mui-focused fieldset': { borderColor: manageCategoryConfirmText === habit?.habitDisplayName ? '#f44336' : '#32CA1D' },
                },
              }}
            />
            {manageCategoryError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {manageCategoryError}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {manageCategoryConfirmStep === 1 ? (
          <>
            <Button
              variant="outlined"
              onClick={handleManageCategoryCancel}
              sx={{ borderColor: 'grey.600', color: 'grey.300', '&:hover': { borderColor: 'grey.400', bgcolor: 'rgba(255,255,255,0.05)' } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setManageCategoryConfirmStep(2)}
              sx={{ bgcolor: '#f44336', color: '#fff', '&:hover': { bgcolor: '#d32f2f' } }}
            >
              Yes, continue
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={handleManageCategoryCancel}
              disabled={isManageCategorySaving}
              sx={{ borderColor: 'grey.600', color: 'grey.300', '&:hover': { borderColor: 'grey.400', bgcolor: 'rgba(255,255,255,0.05)' } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleManageCategoryConfirm}
              disabled={isManageCategorySaving || manageCategoryConfirmText !== habit?.habitDisplayName}
              startIcon={isManageCategorySaving ? <CircularProgress size={16} /> : <DeleteIcon />}
              sx={{ bgcolor: '#f44336', color: '#fff', '&:hover': { bgcolor: '#d32f2f' }, '&.Mui-disabled': { bgcolor: 'grey.700', color: 'grey.500' } }}
            >
              {isManageCategorySaving ? 'Saving...' : 'Change Category'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
    <Snackbar
      open={versionCreationError !== null}
      autoHideDuration={6000}
      onClose={() => setVersionCreationError(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => setVersionCreationError(null)} severity={versionSnackbarSeverity} sx={{ width: '100%' }}>
        {versionCreationError}
      </Alert>
    </Snackbar>
    </>
  );
};

export default HabitDetailModal;
